import { useEffect, useState, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Row,
  Col,
  Tree,
  Button,
  Table,
  Input,
  Select,
  TreeSelect,
  Space,
  Tag,
  Modal,
  Form,
  message,
  Empty,
  Typography,
  Drawer,
  Popconfirm,
  Dropdown,
  Checkbox,
  AutoComplete,
  Tabs,
  Upload,
  Tooltip,
  Radio,
  Spin,
} from 'antd'
import type { MenuProps } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import {
  PlusOutlined,
  FolderOutlined,
  DeleteOutlined,
  BranchesOutlined,
  ApiOutlined,
  ThunderboltOutlined,
  ClockCircleOutlined,
  HolderOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  RightOutlined,
  DownOutlined,
  CodeOutlined,
  MergeCellsOutlined,
  FormOutlined,
  ShareAltOutlined,
  AppstoreOutlined,
  MenuOutlined,
  ImportOutlined,
  CheckCircleOutlined,
  UploadOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons'
import type { TreeDataNode } from 'antd'
import * as apiDefApi from '@/api/apiDefinitions'
import { post } from '@/utils/request'
import * as scenarioApi from '@/api/scenarios'
import * as environmentApi from '@/api/environments'
import * as reportApi from '@/api/reports'
import dayjs from 'dayjs'
import type { Scenario, ScenarioModule, ScenarioStep, ScenarioStepType, Environment, ApiDefinition } from '@/types'
import { useProjectStore } from '@/stores/project'
import { useAuthStore } from '@/stores/auth'
import { useThemeStore } from '@/stores/theme'
import '../../../styles/aurora-page.css'
import '../../../styles/aurora-modal.css'

const { Title, Text } = Typography

/** 场景 step 步骤类型配置 */
const stepTypeConfig: Record<ScenarioStepType, { label: string; icon: React.ReactNode; color: string }> = {
  http: { label: 'HTTP 请求', icon: <ApiOutlined />, color: 'var(--au-primary)' },
  wait: { label: '等待控制', icon: <ClockCircleOutlined />, color: '#6b7280' },
  condition: { label: '条件控制', icon: <BranchesOutlined />, color: '#f59e0b' },
  loop: { label: '循环控制', icon: <MenuOutlined style={{ transform: 'rotate(90deg)' }} />, color: '#8b5cf6' },
  assertion: { label: '断言控制', icon: <ThunderboltOutlined />, color: '#f59e0b' },
  db: { label: '数据库操作', icon: <AppstoreOutlined />, color: '#10b981' },
  script: { label: '脚本操作', icon: <CodeOutlined />, color: '#ef4444' },
}

const stepTypeOptions = Object.entries(stepTypeConfig).map(([key, val]) => ({
  value: key,
  label: (
    <Space>
      <span style={{ color: val.color }}>{val.icon}</span>
      <span>{val.label}</span>
    </Space>
  ),
}))

const methodColor: Record<string, string> = {
  GET: 'blue',
  POST: 'success',
  PUT: 'orange',
  DELETE: 'red',
  PATCH: 'purple',
}

const methodPathStyle: Record<string, { bg: string; color: string }> = {
  GET: { bg: '#dbeafe', color: '#1d4ed8' },
  POST: { bg: '#dcfce7', color: '#166534' },
  PUT: { bg: '#ffedd5', color: '#c2410c' },
  DELETE: { bg: '#fee2e2', color: '#dc2626' },
  PATCH: { bg: '#ede9fe', color: '#7c3aed' },
}

const methodOptions = ['GET', 'POST', 'PUT', 'DELETE', 'PATCH'].map((m) => ({ value: m, label: m }))

const bodyTypeOptions = [
  { value: 'none', label: 'none' },
  { value: 'form-data', label: 'form-data' },
  { value: 'x-www-form-urlencoded', label: 'x-www-form-urlencoded' },
  { value: 'json', label: 'json' },
  { value: 'xml', label: 'xml' },
  { value: 'raw', label: 'raw' },
  { value: 'binary', label: 'binary' },
]

const executionStatusColor: Record<string, string> = {
  pending: 'default',
  running: 'processing',
  passed: 'success',
  failed: 'error',
}

const executionStatusText: Record<string, string> = {
  pending: '待执行',
  running: '执行中',
  passed: '通过',
  failed: '失败',
}

const environmentColor: Record<string, string> = {
  dev: 'blue',
  test: 'orange',
  staging: 'purple',
  prod: 'red',
}

const envTagColors = ['blue', 'orange', 'purple', 'red', 'green', 'cyan', 'magenta', 'geekblue', 'gold']

type StepCreateAction =
  | 'import_api'
  | 'new_request'
  | 'wait'
  | 'condition'
  | 'loop'
  | 'assertion'
  | 'db'
  | 'script'

const stepCreateMenuItems: MenuProps['items'] = [
  { key: 'import_api', label: '接口导入', icon: <ApiOutlined /> },
  { key: 'new_request', label: '新增请求', icon: <PlusOutlined /> },
  { type: 'divider' },
  { key: 'wait', label: '等待控制', icon: <ClockCircleOutlined /> },
  { key: 'condition', label: '条件控制', icon: <BranchesOutlined /> },
  { key: 'loop', label: '循环控制', icon: <MenuOutlined style={{ transform: 'rotate(90deg)' }} /> },
  { key: 'assertion', label: '断言控制', icon: <ThunderboltOutlined /> },
  { key: 'db', label: '数据库操作', icon: <AppstoreOutlined /> },
  { key: 'script', label: '脚本操作', icon: <CodeOutlined /> },
]

// 子步骤菜单：与主添加步骤菜单一致（含接口导入）
const childStepCreateMenuItems: MenuProps['items'] = [
  { key: 'import_api', label: '接口导入', icon: <ApiOutlined /> },
  { key: 'new_request', label: '新增请求', icon: <PlusOutlined /> },
  { type: 'divider' },
  { key: 'wait', label: '等待控制', icon: <ClockCircleOutlined /> },
  { key: 'condition', label: '条件控制', icon: <BranchesOutlined /> },
  { key: 'loop', label: '循环控制', icon: <MenuOutlined style={{ transform: 'rotate(90deg)' }} /> },
  { key: 'assertion', label: '断言控制', icon: <ThunderboltOutlined /> },
  { key: 'db', label: '数据库操作', icon: <AppstoreOutlined /> },
  { key: 'script', label: '脚本操作', icon: <CodeOutlined /> },
]

const actionToStepType: Record<StepCreateAction, number> = {
  import_api: 1,
  new_request: 1,
  wait: 2,
  condition: 3,
  loop: 4,
  assertion: 5,
  db: 6,
  script: 7,
}

const actionToStepTypeLabel: Record<StepCreateAction, string> = {
  import_api: '接口导入',
  new_request: '新增请求',
  wait: '等待控制',
  condition: '条件控制',
  loop: '循环控制',
  assertion: '断言控制',
  db: '数据库操作',
  script: '脚本操作',
}

const stepTypeNumberToLabel: Record<number, ScenarioStepType> = {
  1: 'http',
  2: 'wait',
  3: 'condition',
  4: 'loop',
  5: 'assertion',
  6: 'db',
  7: 'script',
}

/** 等待指定毫秒 */
const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms))

/** 根据环境名取一个稳定的标签颜色 */
const getEnvColor = (name: string): string => {
  if (environmentColor[name]) return environmentColor[name]
  if (!name) return 'default'
  let sum = 0
  for (let i = 0; i < name.length; i++) sum += name.charCodeAt(i)
  return envTagColors[sum % envTagColors.length]
}

const kvArrayToJson = (entries: { key: string; value: string; remark: string }[]): string => {
  const filtered = entries.filter((e) => e.key.trim() !== '')
  if (filtered.length === 0) return '[]'
  return JSON.stringify(filtered)
}

const jsonToKvArray = (jsonStr: string): { key: string; value: string; remark: string }[] => {
  try {
    const parsed = JSON.parse(jsonStr || '[]')
    if (Array.isArray(parsed)) return parsed
    if (typeof parsed === 'object' && parsed !== null) {
      return Object.entries(parsed).map(([key, value]) => ({
        key,
        value: String(value ?? ''),
        remark: '',
      }))
    }
    return []
  } catch {
    return []
  }
}

const loadBodyFormEntries = (bodyStr: string, bodyType: string): { key: string; value: string; remark: string }[] => {
  if (bodyType !== 'form-data' && bodyType !== 'x-www-form-urlencoded') return []
  try {
    const parsed = JSON.parse(bodyStr || '[]')
    if (Array.isArray(parsed)) {
      return parsed.map((e: any) => ({
        key: e.key || '',
        value: ['string', 'integer', 'number', 'boolean', 'array', 'json', 'file'].includes(e.type) ? e.type : 'string',
        remark: e.value || e.remark || '',
      }))
    }
    // 兼容旧的对象格式数据：{ "username": "18211111111" }
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return Object.entries(parsed).map(([k, v]) => ({
        key: k,
        value: 'string',
        remark: String(v ?? ''),
      }))
    }
  } catch {
    // ignore
  }
  return []
}

const buildDisplayUrl = (record: ApiDefinition): string => {
  let displayPath = record.path || ''
  const queryIndex = displayPath.indexOf('?')
  if (queryIndex !== -1) {
    displayPath = displayPath.slice(0, queryIndex)
  }
  const restParams = jsonToKvArray(record.rest_params || '[]')
  restParams.filter((e) => e.key.trim()).forEach((e) => {
    displayPath = displayPath.replace(new RegExp(`{${e.key.trim()}}`, 'g'), e.value || '')
  })
  const queryParams = jsonToKvArray(record.query_params || '[]')
  const queryParts = queryParams
    .filter((e) => e.key.trim())
    .map((e) => `${encodeURIComponent(e.key.trim())}=${encodeURIComponent(e.value || '')}`)
  if (queryParts.length > 0) {
    displayPath += '?' + queryParts.join('&')
  }
  return displayPath
}

const renderKvTable = (
  name: string,
  placeholders: { key: string; value: string; remark: string },
  keySuggestions?: { value: string; label: string }[],
  isDark?: boolean,
  typeOptions?: { value: string; label: string }[],
  typePlaceholder?: string,
) => (
  <Form.List name={name}>
    {(fields, { add, remove }) => (
      <div style={{ border: isDark ? '1px solid var(--border)' : '1px solid var(--au-border)', borderRadius: 4, overflow: 'hidden' }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: typeOptions ? '40px 1fr 100px 1fr 1.2fr 60px' : '40px 1fr 1fr 1.2fr 60px',
          gap: 0,
          padding: '8px 12px',
          background: isDark ? 'var(--bg)' : 'var(--au-header-bg)',
          borderBottom: isDark ? '1px solid var(--border)' : '1px solid var(--au-border)',
          color: isDark ? 'var(--text-light)' : 'var(--au-slate)',
          fontWeight: 500,
          fontSize: 13,
          alignItems: 'center',
        }}>
          <Checkbox disabled defaultChecked />
          <span>参数名称</span>
          {typeOptions && <span>参数类型</span>}
          <span>参数值</span>
          <span>描述</span>
          <Button type="link" size="small" onClick={() => add({ key: '', ...(typeOptions ? { type: typeOptions[0]?.value || '' } : {}), value: '', remark: '' })} style={{ padding: 0 }}>
            添加
          </Button>
        </div>

        {fields.length === 0 && (
          <div style={{ padding: '24px 0', color: isDark ? 'var(--text-lighter)' : 'var(--au-slate-muted)', fontSize: 13, textAlign: 'center' }}>
            暂无数据，点击右上角"添加"新增
          </div>
        )}

        {fields.map(({ key, name: fieldName, ...restField }, index) => (
          <div
            key={key}
            style={{
              display: 'grid',
              gridTemplateColumns: typeOptions ? '40px 1fr 100px 1fr 1.2fr 60px' : '40px 1fr 1fr 1.2fr 60px',
              gap: 0,
              padding: '4px 12px',
              alignItems: 'center',
              borderBottom: isDark ? '1px solid var(--border)' : '1px solid var(--au-row-alt)',
              background: isDark
                ? (index % 2 === 0 ? 'var(--bg)' : 'var(--card)')
                : (index % 2 === 0 ? '#ffffff' : 'var(--au-header-bg)'),
            }}
          >
            <Checkbox defaultChecked />
            <Form.Item {...restField} name={[fieldName, 'key']} style={{ marginBottom: 0, marginRight: 8 }}>
              {keySuggestions ? (
                <AutoComplete
                  size="small"
                  options={keySuggestions}
                  filterOption={(inputValue, option) =>
                    option!.value.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
                  }
                  placeholder={placeholders.key}
                  style={{ width: '100%' }}
                />
              ) : (
                <Input size="small" placeholder={placeholders.key} bordered={false} />
              )}
            </Form.Item>
            {typeOptions && (
              <Form.Item {...restField} name={[fieldName, 'type']} style={{ marginBottom: 0, marginRight: 8 }}>
                <Select size="small" bordered={false} options={typeOptions} style={{ width: '100%' }} />
              </Form.Item>
            )}
            <Form.Item {...restField} name={[fieldName, 'value']} style={{ marginBottom: 0, marginRight: 8 }}>
              <Input size="small" placeholder={placeholders.value} bordered={false} style={{ background: isDark ? 'var(--bg)' : undefined, color: isDark ? 'var(--text)' : undefined }} />
            </Form.Item>
            <Form.Item {...restField} name={[fieldName, 'remark']} style={{ marginBottom: 0, marginRight: 8 }}>
              <Input size="small" placeholder={placeholders.remark} bordered={false} style={{ background: isDark ? 'var(--bg)' : undefined, color: isDark ? 'var(--text)' : undefined }} />
            </Form.Item>
            <Button type="text" size="small" danger onClick={() => remove(fieldName)} style={{ padding: 0 }}>
              删除
            </Button>
          </div>
        ))}
      </div>
    )}
  </Form.List>
)

export default function Scenarios() {
  const { isDark } = useThemeStore()
  const [modules, setModules] = useState<ScenarioModule[]>([])
  const [envList, setEnvList] = useState<Environment[]>([])
  const [scenarios, setScenarios] = useState<Scenario[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [selectedModule, setSelectedModule] = useState<number | null>(null)
  const [keyword, setKeyword] = useState('')
  const [searchKeyword, setSearchKeyword] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)

  // 模块操作
  const [moduleModalOpen, setModuleModalOpen] = useState(false)
  const [editingModule, setEditingModule] = useState<ScenarioModule | null>(null)
  const [moduleForm] = Form.useForm()
  const [contextMenuModule, setContextMenuModule] = useState<ScenarioModule | null>(null)

  // 场景创建/编辑 Drawer
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editingScenario, setEditingScenario] = useState<Scenario | null>(null)
  const [scenarioForm] = Form.useForm()
  const [submitting, setSubmitting] = useState(false)

  // 步骤编辑
  const [steps, setSteps] = useState<ScenarioStep[]>([])
  const [stepsLoading, setStepsLoading] = useState(false)
  const [selectedStepId, setSelectedStepId] = useState<number | null>(null)
  const [stepForm] = Form.useForm()
  const watchedStepType = Form.useWatch('step_type', stepForm)

  const applyStepHost = () => {
    if (editingScenario?.environment) {
      stepForm.setFieldsValue({ host: editingScenario.environment })
    }
  }

  // 临时步骤数据快照：保存每个未保存步骤的表单数据，便于切换时恢复
  // 使用 ref 确保读写同步，避免 React 异步批处理导致读到旧值
  type TempStepSnapshot = {
    api: ApiDefinition | null
    detailMode: 'api_detail' | 'new_request' | 'step_form'
    formValues: Record<string, any>
    assertionRules: { id: string; type: string; operator: string; expected: string; key: string; expression: string }[]
    extractRules: { id: string; name: string; method: string; expression: string; description: string }[]
    bodyType: string
  }
  const tempStepSnapshotsRef = useRef<Record<number, TempStepSnapshot>>({})
  // 同步 selectedStepId 的最新值，防止异步批处理导致重复创建步骤
  const selectedStepIdRef = useRef<number | null>(null)
  const setSelectedStepIdSync = (id: number | null) => {
    selectedStepIdRef.current = id
    setSelectedStepId(id)
  }

  // 保存当前选中的临时步骤表单数据到快照（同步写入 ref）
  const saveCurrentTempSnapshot = () => {
    if (selectedStepId !== null && selectedStepId < 0) {
      const currentStep = steps.find((s) => s.id === selectedStepId)
      if (currentStep) {
        tempStepSnapshotsRef.current[selectedStepId] = {
          api: selectedImportApi,
          detailMode: stepDetailMode,
          formValues: stepForm.getFieldsValue(true),
          assertionRules: stepAssertionRules,
          extractRules: stepExtractRules,
          bodyType: stepBodyType,
        }
      }
    }
  }

  // 接口导入
  const [apiImportModalOpen, setApiImportModalOpen] = useState(false)
  const [apiList, setApiList] = useState<ApiDefinition[]>([])
  const [apiListLoading, setApiListLoading] = useState(false)
  const [apiListPage, setApiListPage] = useState(1)
  const [apiListTotal, setApiListTotal] = useState(0)
  const [selectedImportApi, setSelectedImportApi] = useState<ApiDefinition | null>(null)

  // 步骤详情模式
  const [stepDetailMode, setStepDetailMode] = useState<'step_form' | 'api_detail' | 'new_request'>('step_form')
  // 步骤详情加载中状态（首次加载时显示 loading 而非空表单，避免闪现）
  const [stepDetailLoading, setStepDetailLoading] = useState(false)
  // 已加载步骤详情缓存：stepId -> { detail, api }，切换步骤时优先读缓存实现一步到位渲染
  const stepDetailCacheRef = useRef<Map<number, { detail: any; api: ApiDefinition | null }>>(new Map())

  // 步骤详情接口编辑器状态
  const [stepActiveTab, setStepActiveTab] = useState('headers')
  const [stepBodyType, setStepBodyType] = useState('none')
  const [stepDebugging, setStepDebugging] = useState(false)
  const [stepResponseResult, setStepResponseResult] = useState<{ status: number; body: string; headers: Record<string, string>; elapsed_ms: number } | null>(null)
  const [stepResponseTab, setStepResponseTab] = useState<'body' | 'headers' | 'assert' | 'extract'>('body')
  const [stepAssertionRules, setStepAssertionRules] = useState<{ id: string; type: string; operator: string; expected: string; key: string; expression: string }[]>([])
  const [stepAssertionResults, setStepAssertionResults] = useState<{ id: string; passed: boolean; message: string }[]>([])
  const assertionIdRef = useRef(0)
  // 参数提取规则：提取响应中的参数供后续步骤使用
  const [stepExtractRules, setStepExtractRules] = useState<{ id: string; name: string; method: string; expression: string; description: string }[]>([])
  // 参数提取结果：调试后展示每条规则的提取情况
  const [stepExtractResults, setStepExtractResults] = useState<{ id: string; success: boolean; value: string; message: string }[]>([])
  const extractIdRef = useRef(0)
  // 场景变量池：按步骤执行顺序累积，供后续步骤使用 ${变量名} 引用
  // - 调试单步时为空（仅使用当前步骤之前的步骤提取的变量）
  // - 运行场景时按顺序填充
  const scenarioVariablesRef = useRef<Map<string, string>>(new Map())
  // 最近聚焦的可编辑元素（供「引用变量」快速插入到光标处）
  const lastFocusedEditableRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null)
  // 各步骤已保存的参数提取规则缓存（step_id -> extract_rules JSON），供「引用变量」无需运行/逐个点开即可列出
  const extractRulesCacheRef = useRef<Map<number, string>>(new Map())

  // 条件表达式求值：支持 ${var} op value 形式，op ∈ ==、!=、>、>=、<、<=、contains
  const evaluateConditionExpression = (expression: string, variables: Map<string, string>): boolean => {
    const expr = applyVariables(expression || '', variables).trim()
    if (!expr) return true
    const match = expr.match(/^(.+?)\s*(==|!=|>=|<=|>|<|contains)\s*(.+)$/)
    if (!match) {
      // 无操作符：非空字符串/非 "false"/非 "0" 视为真
      return expr !== '' && expr !== 'false' && expr !== '0'
    }
    const [, leftRaw, op, rightRaw] = match
    const left = leftRaw.trim().replace(/^["']|["']$/g, '')
    const right = rightRaw.trim().replace(/^["']|["']$/g, '')
    const leftNum = parseFloat(left)
    const rightNum = parseFloat(right)
    const bothNumeric = !isNaN(leftNum) && !isNaN(rightNum)
    switch (op) {
      case '==': return bothNumeric ? leftNum === rightNum : left === right
      case '!=': return bothNumeric ? leftNum !== rightNum : left !== right
      case '>': return bothNumeric ? leftNum > rightNum : false
      case '>=': return bothNumeric ? leftNum >= rightNum : false
      case '<': return bothNumeric ? leftNum < rightNum : false
      case '<=': return bothNumeric ? leftNum <= rightNum : false
      case 'contains': return left.includes(right)
      default: return false
    }
  }
  // 步骤执行状态：stepId -> { status: 'pending'|'running'|'success'|'failed'|'skipped', variables?: Record<string, string> }
  const [stepRunStatus, setStepRunStatus] = useState<Record<number, { status: 'pending' | 'running' | 'success' | 'failed' | 'error' | 'skipped'; variables?: Record<string, string>; message?: string }>>({})

  // 控制/断言步骤的子步骤区域展开状态（默认收起）
  const [expandedStepIds, setExpandedStepIds] = useState<Record<number, boolean>>({})
  // 接口导入的目标父步骤（null 表示导入为顶层步骤）
  const [importParentStepId, setImportParentStepId] = useState<number | null>(null)
  // 拖拽投放指示：stepId -> 'before'（顶部插入线）/ 'inside'（放入控制步骤作用域）
  const [dropIndicator, setDropIndicator] = useState<{ stepId: number; mode: 'before' | 'inside' } | null>(null)
  const dropModeRef = useRef<'before' | 'inside'>('before')
  // 运行场景时各步骤的响应结果：stepId -> response，切换步骤时保持展示，仅退出编辑场景时清空
  const [stepRunResults, setStepRunResults] = useState<Record<number, { status: number; body: string; headers: Record<string, string>; elapsed_ms: number }>>({})
  // 运行场景时各步骤的断言结果：stepId -> results，切换步骤时保持展示
  const [stepRunAssertionResults, setStepRunAssertionResults] = useState<Record<number, { id: string; passed: boolean; message: string }[]>>({})
  // 运行场景时各步骤的参数提取结果：stepId -> results，切换步骤时保持展示
  const [stepRunExtractResults, setStepRunExtractResults] = useState<Record<number, { id: string; success: boolean; value: string; message: string }[]>>({})
  // 场景运行中状态
  const [scenarioRunning, setScenarioRunning] = useState(false)
  // 步骤拖拽排序：当前被拖拽的步骤 id
  const dragStepIdRef = useRef<number | null>(null)
  // 拖拽放置目标索引（用于显示蓝色指示线）
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null)

  // 变量替换：将字符串中的 ${变量名} 替换为变量池中的值
  // 支持嵌套字段，如 ${user.token}
  const applyVariables = (str: string, variables: Map<string, string>): string => {
    if (!str || typeof str !== 'string') return str
    return str.replace(/\$\{([^}]+)\}/g, (match, varName) => {
      const name = varName.trim()
      if (variables.has(name)) {
        return variables.get(name) || ''
      }
      // 未找到变量，保留原样（便于发现配置错误）
      return match
    })
  }

  // 递归替换对象/数组/字符串中的变量
  const applyVariablesDeep = (obj: any, variables: Map<string, string>): any => {
    if (obj === null || obj === undefined) return obj
    if (typeof obj === 'string') return applyVariables(obj, variables)
    if (Array.isArray(obj)) return obj.map((item) => applyVariablesDeep(item, variables))
    if (typeof obj === 'object') {
      const result: Record<string, any> = {}
      for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
          result[key] = applyVariablesDeep(obj[key], variables)
        }
      }
      return result
    }
    return obj
  }

  const stepAssertionTypeOptions = [
    { value: 'status_code', label: '状态码' },
    { value: 'response_header', label: '响应头' },
    { value: 'response_body', label: '响应体' },
    { value: 'response_time', label: '响应时间' },
  ]

  const stepAssertionOperatorMap: Record<string, { value: string; label: string }[]> = {
    status_code: [
      { value: 'equals', label: '等于' },
      { value: 'not_equals', label: '不等于' },
    ],
    response_header: [
      { value: 'equals', label: '等于' },
      { value: 'not_equals', label: '不等于' },
      { value: 'contains', label: '包含' },
      { value: 'not_contains', label: '不包含' },
    ],
    response_body: [
      { value: 'equals', label: '等于' },
      { value: 'not_equals', label: '不等于' },
      { value: 'greater_than', label: '大于' },
      { value: 'greater_than_or_equal', label: '大于等于' },
      { value: 'less_than', label: '小于' },
      { value: 'less_than_or_equal', label: '小于等于' },
      { value: 'contains', label: '包含' },
      { value: 'not_contains', label: '不包含' },
      { value: 'starts_with', label: '以...开始' },
      { value: 'ends_with', label: '以...结束' },
      { value: 'is_empty', label: '为空' },
      { value: 'is_not_empty', label: '不为空' },
      { value: 'regex_match', label: '正则匹配' },
      { value: 'length_equals', label: '长度等于' },
      { value: 'length_greater_than', label: '长度大于' },
      { value: 'length_less_than', label: '长度小于' },
      { value: 'field_exists', label: '字段存在' },
      { value: 'field_not_exists', label: '字段不存在' },
      { value: 'is_null', label: '为null' },
      { value: 'is_not_null', label: '不为null' },
      { value: 'type_is', label: '类型为' },
    ],
    response_time: [
      { value: 'less_than', label: '小于' },
      { value: 'greater_than', label: '大于' },
    ],
  }

  const stepParamTypeOptions = ['string', 'integer', 'number', 'boolean', 'array'].map((t) => ({ value: t, label: t }))
  const stepFormDataTypeOptions = ['string', 'integer', 'number', 'boolean', 'array', 'json', 'file'].map((t) => ({ value: t, label: t }))
  const stepUrlencodedTypeOptions = ['string', 'integer', 'number', 'boolean', 'array'].map((t) => ({ value: t, label: t }))

  const stepCommonHeaders = [
    'Content-Type', 'Authorization', 'Accept', 'User-Agent',
    'Cookie', 'Referer', 'Origin', 'Accept-Language', 'Accept-Encoding',
    'Cache-Control', 'Connection', 'Host', 'X-Requested-With', 'If-Modified-Since',
    'If-None-Match', 'X-CSRF-Token', 'X-Forwarded-For',
  ].map((h) => ({ value: h, label: h }))

  const resolveStepJsonPath = (obj: Record<string, unknown>, path: string): unknown => {
    const parts = path.replace(/\[(\d+)\]/g, '.$1').split('.').filter(Boolean)
    let current: Record<string, unknown> | unknown = obj
    for (const part of parts) {
      if (current == null) return undefined
      current = (current as Record<string, unknown>)[part]
    }
    return current
  }

  const evaluateStepAssertions = (rules: { id: string; type: string; operator: string; expected: string; key: string; expression: string }[], res: { status: number; body: string; headers: Record<string, string>; elapsed_ms: number }) => {
    if (!rules || rules.length === 0) {
      setStepAssertionResults([])
      return []
    }
    const results = rules.map((rule) => {
      let passed = false
      let msg = ''
      const expected = rule.expected || ''
      switch (rule.type) {
        case 'status_code': {
          const statusStr = String(res.status)
          if (rule.operator === 'equals') {
            passed = statusStr === expected
            msg = passed ? `状态码等于 ${expected}` : `状态码应为 ${expected}，实际为 ${res.status}`
          } else if (rule.operator === 'not_equals') {
            passed = statusStr !== expected
            msg = passed ? `状态码不等于 ${expected}` : `状态码不应为 ${expected}，实际为 ${res.status}`
          }
          break
        }
        case 'response_header': {
          const headerVal = (res.headers[rule.key] || res.headers[rule.key.toLowerCase()] || '').toString()
          if (rule.operator === 'equals') {
            passed = headerVal === expected
            msg = passed ? `响应头 ${rule.key} 等于 ${expected}` : `响应头 ${rule.key} 应为 ${expected}，实际为 ${headerVal || '(空)'}`
          } else if (rule.operator === 'not_equals') {
            passed = headerVal !== expected
            msg = passed ? `响应头 ${rule.key} 不等于 ${expected}` : `响应头 ${rule.key} 不应为 ${expected}，实际为 ${headerVal || '(空)'}`
          } else if (rule.operator === 'contains') {
            passed = headerVal.includes(expected)
            msg = passed ? `响应头 ${rule.key} 包含 ${expected}` : `响应头 ${rule.key} 应包含 ${expected}，实际为 ${headerVal || '(空)'}`
          } else if (rule.operator === 'not_contains') {
            passed = !headerVal.includes(expected)
            msg = passed ? `响应头 ${rule.key} 不包含 ${expected}` : `响应头 ${rule.key} 不应包含 ${expected}，实际为 ${headerVal || '(空)'}`
          }
          break
        }
        case 'response_body': {
          const rawBody = res.body || ''
          let bodyStr = rawBody
          let extractedRaw: unknown = undefined
          if (rule.expression) {
            try {
              const parsed = JSON.parse(rawBody)
              extractedRaw = resolveStepJsonPath(parsed, rule.expression)
              bodyStr = extractedRaw !== undefined ? String(extractedRaw) : rawBody
            } catch {
              // JSON 解析失败则使用原始 body
            }
          }
          const op = rule.operator
          if (op === 'is_null') {
            passed = !rule.expression || extractedRaw === null
            msg = passed ? '值为null' : '值不为null'
          } else if (op === 'is_not_null') {
            passed = rule.expression ? extractedRaw !== null && extractedRaw !== undefined : bodyStr !== null
            msg = passed ? '值不为null' : '值为null或不存在'
          } else if (op === 'field_exists') {
            passed = extractedRaw !== undefined
            msg = passed ? `字段 "${rule.expression}" 存在` : `字段 "${rule.expression}" 不存在`
          } else if (op === 'field_not_exists') {
            passed = extractedRaw === undefined
            msg = passed ? `字段 "${rule.expression}" 不存在` : `字段 "${rule.expression}" 存在`
          } else if (op === 'type_is') {
            const actualType = extractedRaw === null ? 'null' : extractedRaw === undefined ? 'undefined' : Array.isArray(extractedRaw) ? 'array' : typeof extractedRaw
            passed = actualType === expected.toLowerCase()
            msg = passed ? `类型为 ${expected}` : `类型应为 ${expected}，实际为 ${actualType}`
          } else if (op === 'is_empty') {
            passed = bodyStr === '' || bodyStr === 'null' || bodyStr === 'undefined'
            msg = passed ? '值为空' : `值不为空: ${bodyStr.length > 50 ? bodyStr.slice(0, 50) + '...' : bodyStr}`
          } else if (op === 'is_not_empty') {
            passed = bodyStr !== '' && bodyStr !== 'null' && bodyStr !== 'undefined'
            msg = passed ? '值不为空' : '值为空'
          } else if (op === 'regex_match') {
            try {
              passed = new RegExp(expected).test(bodyStr)
              msg = passed ? `正则 "/${expected}/" 匹配成功` : `正则 "/${expected}/" 不匹配`
            } catch {
              passed = false
              msg = `正则表达式 "/${expected}/" 无效`
            }
          } else if (op === 'contains') {
            passed = bodyStr.includes(expected)
            msg = passed ? `包含 "${expected}"` : `应包含 "${expected}"`
          } else if (op === 'not_contains') {
            passed = !bodyStr.includes(expected)
            msg = passed ? `不包含 "${expected}"` : `不应包含 "${expected}"`
          } else if (op === 'starts_with') {
            passed = bodyStr.startsWith(expected)
            msg = passed ? `以 "${expected}" 开始` : `应以 "${expected}" 开始`
          } else if (op === 'ends_with') {
            passed = bodyStr.endsWith(expected)
            msg = passed ? `以 "${expected}" 结束` : `应以 "${expected}" 结束`
          } else if (op === 'length_equals') {
            const len = bodyStr.length
            const expLen = parseInt(expected, 10)
            passed = len === expLen
            msg = passed ? `长度等于 ${expLen}` : `长度应为 ${expLen}，实际为 ${len}`
          } else if (op === 'length_greater_than') {
            const len = bodyStr.length
            const expLen = parseInt(expected, 10)
            passed = len > expLen
            msg = passed ? `长度大于 ${expLen}` : `长度应大于 ${expLen}，实际为 ${len}`
          } else if (op === 'length_less_than') {
            const len = bodyStr.length
            const expLen = parseInt(expected, 10)
            passed = len < expLen
            msg = passed ? `长度小于 ${expLen}` : `长度应小于 ${expLen}，实际为 ${len}`
          } else if (op === 'equals') {
            passed = String(bodyStr) === String(expected)
            msg = passed ? `等于 ${expected}` : `应为 ${expected}，实际为 ${bodyStr}`
          } else if (op === 'not_equals') {
            passed = String(bodyStr) !== String(expected)
            msg = passed ? `不等于 ${expected}` : `不应等于 ${expected}`
          } else {
            const numVal = parseFloat(bodyStr)
            const numExp = parseFloat(expected)
            if (op === 'greater_than') {
              passed = !isNaN(numVal) && numVal > numExp
              msg = passed ? `${numVal} > ${numExp}` : `${numVal} 应大于 ${numExp}`
            } else if (op === 'greater_than_or_equal') {
              passed = !isNaN(numVal) && numVal >= numExp
              msg = passed ? `${numVal} >= ${numExp}` : `${numVal} 应大于等于 ${numExp}`
            } else if (op === 'less_than') {
              passed = !isNaN(numVal) && numVal < numExp
              msg = passed ? `${numVal} < ${numExp}` : `${numVal} 应小于 ${numExp}`
            } else if (op === 'less_than_or_equal') {
              passed = !isNaN(numVal) && numVal <= numExp
              msg = passed ? `${numVal} <= ${numExp}` : `${numVal} 应小于等于 ${numExp}`
            } else {
              msg = `未知操作符: ${op}`
            }
          }
          break
        }
        case 'response_time': {
          const time = res.elapsed_ms
          if (rule.operator === 'less_than') {
            const limit = parseFloat(expected)
            passed = time < limit
            msg = passed ? `响应时间 ${time}ms < ${limit}ms` : `响应时间 ${time}ms 应小于 ${limit}ms`
          } else if (rule.operator === 'greater_than') {
            const limit = parseFloat(expected)
            passed = time > limit
            msg = passed ? `响应时间 ${time}ms > ${limit}ms` : `响应时间 ${time}ms 应大于 ${limit}ms`
          }
          break
        }
      }
      return { id: rule.id, passed, message: msg, type: rule.type, operator: rule.operator, expected, key: rule.key, expression: rule.expression }
    })
    setStepAssertionResults(results)
    return results
  }

  const highlightStepJson = (text: string): React.ReactNode => {
    try {
      JSON.parse(text)
      const parts: React.ReactNode[] = []
      let lastIndex = 0
      const regex = /("(?:[^"\\]|\\.)*")\s*:/g
      let match
      while ((match = regex.exec(text)) !== null) {
        if (match.index > lastIndex) {
          const before = text.slice(lastIndex, match.index)
          parts.push(...colorizeStepFragment(before, `f${match.index}-`))
        }
        parts.push(<span key={`k${match.index}`} style={{ color: '#0369a1' }}>{match[1]}</span>)
        parts.push(<span key={`c${match.index}`} style={{ color: 'var(--au-text-faint)' }}>:</span>)
        lastIndex = match.index + match[0].length
      }
      if (lastIndex < text.length) {
        parts.push(...colorizeStepFragment(text.slice(lastIndex), 'tail-'))
      }
      return <>{parts}</>
    } catch {
      return colorizeStepXml(text)
    }
  }

  const colorizeStepFragment = (fragment: string, keyPrefix: string): React.ReactNode[] => {
    const result: React.ReactNode[] = []
    let remaining = fragment
    let key = 0
    while (remaining.length > 0) {
      const strMatch = remaining.match(/^("(?:[^"\\]|\\.)*")/)
      const numMatch = remaining.match(/^(-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)/)
      const boolMatch = remaining.match(/^(true|false)/)
      const nullMatch = remaining.match(/^(null)/)

      if (strMatch) {
        result.push(<span key={`${keyPrefix}s${key++}`} style={{ color: '#16a34a' }}>{strMatch[1]}</span>)
        remaining = remaining.slice(strMatch[0].length)
      } else if (numMatch) {
        result.push(<span key={`${keyPrefix}n${key++}`} style={{ color: '#d97706' }}>{numMatch[1]}</span>)
        remaining = remaining.slice(numMatch[0].length)
      } else if (boolMatch) {
        result.push(<span key={`${keyPrefix}b${key++}`} style={{ color: '#7c3aed', fontWeight: 600 }}>{boolMatch[1]}</span>)
        remaining = remaining.slice(boolMatch[0].length)
      } else if (nullMatch) {
        result.push(<span key={`${keyPrefix}l${key++}`} style={{ color: 'var(--au-text-faint)', fontStyle: 'italic' }}>{nullMatch[1]}</span>)
        remaining = remaining.slice(nullMatch[0].length)
      } else {
        const next = remaining[0]
        result.push(<span key={`${keyPrefix}p${key++}`}>{next}</span>)
        remaining = remaining.slice(1)
      }
    }
    return result
  }

  const colorizeStepXml = (text: string): React.ReactNode => {
    const parts: React.ReactNode[] = []
    let lastIndex = 0
    const regex = /(<\/?[\w:-]+)([^>]*)(\/?>)|([^<]+)/g
    let match
    let key = 0
    while ((match = regex.exec(text)) !== null) {
      if (match[4]) {
        parts.push(<span key={key++} style={{ color: 'var(--au-slate)' }}>{match[4]}</span>)
      } else {
        parts.push(<span key={key++} style={{ color: '#d946ef' }}>{'<'}{match[1]}</span>)
        if (match[2]) {
          const attrRegex = /(\s+)([\w:-]+)(="([^"]*)")?/g
          let attrMatch
          let attrLast = 0
          while ((attrMatch = attrRegex.exec(match[2])) !== null) {
            if (attrMatch.index > attrLast) {
              parts.push(<span key={key++}>{match[2].slice(attrLast, attrMatch.index)}</span>)
            }
            parts.push(<span key={key++}>{attrMatch[1]}</span>)
            parts.push(<span key={key++} style={{ color: '#d97706' }}>{attrMatch[2]}</span>)
            if (attrMatch[3]) {
              parts.push(<span key={key++} style={{ color: 'var(--au-text-faint)' }}>=</span>)
              parts.push(<span key={key++} style={{ color: '#16a34a' }}>{attrMatch[4]}</span>)
            }
            attrLast = attrMatch.index + attrMatch[0].length
          }
          if (attrLast < match[2].length) {
            parts.push(<span key={key++}>{match[2].slice(attrLast)}</span>)
          }
        }
        parts.push(<span key={key++} style={{ color: '#d946ef' }}>{match[3]}</span>)
      }
    }
    return <>{parts}</>
  }

  // 规则回显补齐 id：缺失/重复的 id 重新分配，并把自增计数器推到已有 id 之上
  // （修复：计数器随抽屉 mount 重置为 0，回显的 assert_0/assert_1 与新加规则撞 id，
  //   updateXxxRule 按 id 匹配会命中多行，改一个预期值其它行跟着变）
  const withUniqueRuleIds = (rules: any[], prefix: string, counter: { current: number }) => {
    const list = Array.isArray(rules) ? rules : []
    const seen = new Set<string>()
    const next = list.map((r: any) => {
      const item = { ...r }
      if (typeof item.id === 'string' && item.id && !seen.has(item.id)) {
        seen.add(item.id)
        return item
      }
      item.id = `${prefix}${counter.current++}`
      seen.add(item.id)
      return item
    })
    for (const rid of seen) {
      const m = new RegExp(`^${prefix}(\\d+)$`).exec(rid)
      if (m) {
        const n = Number(m[1]) + 1
        if (n > counter.current) counter.current = n
      }
    }
    return next
  }

  const addStepAssertionRule = (type: string) => {
    const id = `assert_${assertionIdRef.current++}`
    const operators = stepAssertionOperatorMap[type] || []
    setStepAssertionRules((prev) => [...prev, { id, type, operator: operators[0]?.value || '', expected: '', key: '', expression: '' }])
  }

  const removeStepAssertionRule = (id: string) => {
    setStepAssertionRules((prev) => prev.filter((r) => r.id !== id))
  }

  const updateStepAssertionRule = (id: string, updates: { key?: string; expression?: string; operator?: string; expected?: string }) => {
    setStepAssertionRules((prev) => prev.map((r) => (r.id === id ? { ...r, ...updates } : r)))
  }

  // 参数提取方式选项
  const stepExtractMethodOptions = [
    { value: 'jsonpath', label: 'JSONPath' },
    { value: 'xpath', label: 'XPath' },
    { value: 'regex', label: '正则' },
  ]

  const addStepExtractRule = () => {
    const id = `extract_${extractIdRef.current++}`
    setStepExtractRules((prev) => [...prev, { id, name: '', method: 'jsonpath', expression: '', description: '' }])
  }

  const removeStepExtractRule = (id: string) => {
    setStepExtractRules((prev) => prev.filter((r) => r.id !== id))
  }

  const updateStepExtractRule = (id: string, updates: { name?: string; method?: string; expression?: string; description?: string }) => {
    setStepExtractRules((prev) => prev.map((r) => (r.id === id ? { ...r, ...updates } : r)))
  }

  // 「引用变量」菜单项：聚合前面步骤的参数提取规则名 + 调试/运行时真实提取到的变量池
  const buildExtractVarMenuItems = (): MenuProps['items'] => {
    const items: MenuProps['items'] = []
    const seen = new Set<string>()
    const pushVar = (name: string, source: string) => {
      const n = (name || '').trim()
      if (!n || seen.has(n)) return
      seen.add(n)
      items.push({
        key: n,
        label: (
          <span>
            <code>{'${' + n + '}'}</code>
            <span style={{ marginLeft: 10, fontSize: 12, color: 'var(--au-slate-muted)' }}>{source}</span>
          </span>
        ),
      })
    }
    // 1) 调试/运行变量池（有真实提取值，最可靠）
    for (const [k, v] of scenarioVariablesRef.current) {
      pushVar(k, `已提取: ${(v || '').slice(0, 24) || '(空)'}`)
    }
    // 2) 按展示顺序（父步骤按 sort，子步骤跟随父步骤）收集前面步骤的提取规则定义
    const sorted = [...steps].sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0))
    for (const parent of sorted.filter((s) => !s.parent_id)) {
      const chain = [parent, ...sorted.filter((s) => s.parent_id === parent.id)]
      for (const s of chain) {
        if (s.id === selectedStepId) continue
        const snap = tempStepSnapshotsRef.current[s.id]
        let rules: { name?: string }[] = snap?.extractRules || []
        if (!rules.length) {
          // 优先取预取/保存时缓存的规则，再退回步骤详情缓存
          const raw = extractRulesCacheRef.current.get(s.id) ?? stepDetailCacheRef.current.get(s.id)?.detail?.extract_rules
          try {
            const parsed = JSON.parse(raw || '[]')
            rules = Array.isArray(parsed) ? parsed : []
          } catch {
            rules = []
          }
        }
        for (const r of rules) pushVar(r?.name || '', `来自步骤: ${s.name}`)
      }
    }
    if (items.length === 0) {
      items.push({ key: '__empty__', label: '暂无可引用变量（先在前面步骤的「参数提取」中配置）', disabled: true })
    }
    return items
  }

  // 插入变量引用到最近聚焦的输入框光标处；无目标时复制到剪贴板
  const insertExtractVariable = (name: string) => {
    const text = '${' + name + '}'
    const el = lastFocusedEditableRef.current
    if (el && document.contains(el)) {
      const isTextarea = el instanceof HTMLTextAreaElement
      const proto = isTextarea ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype
      const setter = Object.getOwnPropertyDescriptor(proto, 'value')?.set
      const start = el.selectionStart ?? el.value.length
      const end = el.selectionEnd ?? el.value.length
      const next = el.value.slice(0, start) + text + el.value.slice(end)
      setter?.call(el, next)
      el.dispatchEvent(new Event('input', { bubbles: true }))
      requestAnimationFrame(() => {
        el.focus()
        try { el.setSelectionRange(start + text.length, start + text.length) } catch { /* noop */ }
      })
      return
    }
    navigator.clipboard?.writeText(text).then(
      () => message.success(`已复制 ${text}，粘贴到任意输入框即可`),
      () => message.info(text),
    )
  }

  // 执行参数提取（调试后自动调用）
  const evaluateStepExtracts = (
    rules: { id: string; name: string; method: string; expression: string; description: string }[],
    res: { status: number; body: string; headers: Record<string, string> },
  ) => {
    if (!rules || rules.length === 0) {
      setStepExtractResults([])
      return
    }
    const results = rules.map((rule) => {
      const { success, value, message } = extractValue(rule, res)
      return { id: rule.id, success, value, message }
    })
    setStepExtractResults(results)
  }

  // 参数提取核心实现：支持 JSONPath / XPath / 正则
  const extractValue = (
    rule: { name: string; method: string; expression: string },
    res: { status: number; body: string; headers: Record<string, string> },
  ): { success: boolean; value: string; message: string } => {
    if (!rule.expression || !rule.expression.trim()) {
      return { success: false, value: '', message: '表达式为空' }
    }
    const body = res.body || ''
    try {
      if (rule.method === 'jsonpath') {
        try {
          const parsed = JSON.parse(body)
          const extracted = resolveStepJsonPath(parsed, rule.expression)
          if (extracted === undefined || extracted === null) {
            return { success: false, value: '', message: '未找到匹配值' }
          }
          const valueStr = typeof extracted === 'object' ? JSON.stringify(extracted) : String(extracted)
          return { success: true, value: valueStr, message: `提取成功: ${valueStr}` }
        } catch {
          return { success: false, value: '', message: '响应体非 JSON 格式，无法使用 JSONPath' }
        }
      }
      if (rule.method === 'regex') {
        const match = new RegExp(rule.expression).exec(body)
        if (!match) {
          return { success: false, value: '', message: '未匹配到内容' }
        }
        const value = match[1] !== undefined ? match[1] : match[0]
        return { success: true, value, message: `提取成功: ${value}` }
      }
      if (rule.method === 'xpath') {
        // XPath 主要用于 XML，JSON 场景退化为 JSONPath 表达式兼容处理
        try {
          const parsed = JSON.parse(body)
          const expr = rule.expression.replace(/^\//, '').replace(/\//g, '.')
          const extracted = resolveStepJsonPath(parsed, expr)
          if (extracted === undefined || extracted === null) {
            return { success: false, value: '', message: '未找到匹配值' }
          }
          const valueStr = typeof extracted === 'object' ? JSON.stringify(extracted) : String(extracted)
          return { success: true, value: valueStr, message: `提取成功: ${valueStr}` }
        } catch {
          return { success: false, value: '', message: '响应体非 JSON 格式，XPath 解析失败' }
        }
      }
      return { success: false, value: '', message: `不支持的提取方式: ${rule.method}` }
    } catch (e) {
      return { success: false, value: '', message: `提取异常: ${(e as Error).message}` }
    }
  }

  /** validateFields 只返回已注册字段；Tabs 懒渲染导致未点开的页签（Query/REST/请求体）字段缺失，
   *  用 getFieldsValue(true) 从 Form Store 补齐未渲染字段的缓存值（已注册字段以校验结果为准） */
  const readStepFormValues = async (): Promise<Record<string, any>> => {
    const values = await stepForm.validateFields()
    const store = stepForm.getFieldsValue(true) as Record<string, any>
    return { ...store, ...values }
  }

  const handleStepDebug = async () => {
    try {
      const values = await readStepFormValues()
      // 引用检查：请求中使用了 ${变量} 但变量池尚无该值时提示（不阻断调试，原样发送）
      const referencedVars = new Set<string>()
      const scanRefs = (s?: string) => {
        for (const m of (s || '').match(/\$\{([^}]+)\}/g) || []) referencedVars.add(m.slice(2, -1))
      }
      scanRefs(values.host)
      scanRefs(values.path)
      scanRefs(values.body)
      for (const kv of [
        ...(values.restEntries || []),
        ...(values.headerEntries || []),
        ...(values.paramEntries || []),
        ...(values.bodyFormEntries || []),
      ]) {
        scanRefs(kv?.value)
      }
      const missingVars = [...referencedVars].filter((n) => !scenarioVariablesRef.current.has(n))
      if (missingVars.length) {
        message.warning(
          `当前请求引用了提取参数 ${missingVars.map((n) => '${' + n + '}').join('、')}，但尚未获取实际值，将按原样发送；如需真实传参，请先运行前面的步骤`,
          5,
        )
      }
      setStepDebugging(true)
      setStepResponseResult(null)
      // 使用当前场景变量池进行变量替换（${变量名}）
      const variables = scenarioVariablesRef.current
      const envUrl = applyVariables(values.host || '', variables)
      let path = applyVariables(values.path || '', variables)
      if (values.restEntries) {
        values.restEntries.filter((e: any) => e.key?.trim()).forEach((e: any) => {
          const restValue = applyVariables(e.value || '', variables)
          path = path.replace(`{${e.key.trim()}}`, restValue)
        })
      }
      const fullUrl = envUrl ? envUrl.replace(/\/+$/, '') + '/' + path.replace(/^\/+/, '') : path

      const headers: Record<string, string> = {}
      if (values.headerEntries) {
        values.headerEntries.filter((e: any) => e.key?.trim()).forEach((e: any) => {
          headers[e.key.trim()] = applyVariables(e.value || '', variables)
        })
      }

      const queryParams: Record<string, string> = {}
      if (values.paramEntries) {
        values.paramEntries.filter((e: any) => e.key?.trim()).forEach((e: any) => {
          queryParams[e.key.trim()] = applyVariables(e.value || '', variables)
        })
      }

      let body: string | FormData | null = null
      const effectiveBodyType = values.body_type || stepBodyType || 'none'
      const bodyFormEntries = values.bodyFormEntries || []
      const bodyValue = values.body || ''
      if (effectiveBodyType === 'form-data' && bodyFormEntries.length) {
        const formData: Record<string, any> = {}
        bodyFormEntries.forEach((entry: any) => {
          if (entry.key) {
            formData[entry.key] = applyVariables(entry.remark || '', variables)
          }
        })
        body = JSON.stringify(formData)
      } else if (effectiveBodyType === 'x-www-form-urlencoded' && bodyFormEntries.length) {
        const params = new URLSearchParams()
        bodyFormEntries.forEach((entry: any) => {
          if (entry.key) {
            params.append(entry.key, applyVariables(entry.remark || '', variables))
          }
        })
        body = params.toString()
      } else if (bodyValue.trim()) {
        body = applyVariables(bodyValue, variables)
        if (effectiveBodyType === 'none') {
          try {
            JSON.parse(bodyValue)
          } catch {
            // keep as raw
          }
        }
      }

      const res = await post<{ status: number; body: string; headers: Record<string, string>; elapsed_ms: number }>('/api-definitions/debug', {
        method: values.method,
        url: fullUrl,
        headers,
        query_params: queryParams,
        body_type: effectiveBodyType,
        body,
      })
      try {
        res.body = JSON.stringify(JSON.parse(res.body), null, 2)
      } catch {
        // non-JSON keep as is
      }
      setStepResponseResult(res)
      evaluateStepAssertions(stepAssertionRules, res)
      evaluateStepExtracts(stepExtractRules, res)
      // 调试时也把提取到的变量存入场景变量池，方便单步调试时验证后续步骤
      stepExtractRules.forEach((rule) => {
        const extracted = extractValue(rule, res)
        if (extracted.success && rule.name) {
          scenarioVariablesRef.current.set(rule.name, extracted.value)
        }
      })
      if (stepAssertionRules.length > 0) {
        setStepResponseTab('assert')
      } else if (stepExtractRules.length > 0) {
        setStepResponseTab('extract')
      }
    } catch (e) {
      console.error('[Scenarios] 调试失败:', e)
    } finally {
      setStepDebugging(false)
    }
  }

  const handleStepFormatResponse = () => {
    if (stepResponseResult) {
      try {
        const parsed = JSON.parse(stepResponseResult.body)
        setStepResponseResult({ ...stepResponseResult, body: JSON.stringify(parsed, null, 2) })
        message.success('已格式化')
      } catch {
        message.warning('响应内容不是有效的 JSON 格式')
      }
    }
  }

  // 步骤拖拽排序：开始拖拽
  const handleDragStart = (e: React.DragEvent, stepId: number) => {
    dragStepIdRef.current = stepId
    e.dataTransfer.effectAllowed = 'move'
    // Firefox 需要 setData 才能触发拖拽
    try { e.dataTransfer.setData('text/plain', String(stepId)) } catch { /* ignore */ }
  }

  // 拖拽经过某个步骤项时，计算放置位置：控制步骤下半部为"放入作用域"，其余为顶部插入线
  const handleDragOver = (e: React.DragEvent, step: ScenarioStep, index: number) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    const dragId = dragStepIdRef.current
    const dragStep = dragId != null ? steps.find((s) => s.id === dragId) : null
    const isScopeStep = ['condition', 'loop', 'assertion'].includes(step.step_type)
    const rect = e.currentTarget.getBoundingClientRect()
    const inBottomHalf = e.clientY > rect.top + rect.height / 2
    if (isScopeStep && inBottomHalf && dragId !== step.id && dragStep?.parent_id !== step.id) {
      dropModeRef.current = 'inside'
      setDragOverIndex(null)
      if (dropIndicator?.stepId !== step.id || dropIndicator.mode !== 'inside') {
        setDropIndicator({ stepId: step.id, mode: 'inside' })
      }
    } else {
      dropModeRef.current = 'before'
      setDropIndicator(null)
      if (dragOverIndex !== index) setDragOverIndex(index)
    }
  }

  // 按顶层步骤 ID 顺序重排：子步骤跟随父步骤整体移动，顶层 sort 重新编号，子步骤 sort 不变
  const reorderTopSteps = async (topIds: number[]) => {
    const childMap = new Map<number, ScenarioStep[]>()
    steps.filter((s) => s.parent_id).forEach((s) => {
      const list = childMap.get(s.parent_id!) || []
      list.push(s)
      childMap.set(s.parent_id!, list)
    })
    const next: ScenarioStep[] = []
    topIds.forEach((id) => {
      const top = steps.find((s) => s.id === id)
      if (top) {
        next.push({ ...top, sort: next.length })
        ;(childMap.get(id) || []).forEach((c) => next.push(c))
      }
    })
    setSteps(next)
    stepsRef.current = next
    // 只持久化顶层顺序（子步骤 sort 独立于顶层）
    if (editingScenario) {
      const orderedIds = topIds.filter((id) => id > 0)
      if (orderedIds.length > 0) {
        try {
          await scenarioApi.reorderSteps(editingScenario.id, orderedIds)
        } catch (err) {
          message.error('顺序保存失败：' + (err as Error).message)
        }
      }
    }
  }

  // 将步骤移入控制步骤的作用域（作为其子步骤追加到末尾）
  const moveStepIntoScope = async (dragId: number, parentId: number) => {
    if (dragId === parentId) return
    const dragStep = steps.find((s) => s.id === dragId)
    if (!dragStep) return
    if (dragStep.parent_id === parentId) return
    if (parentId < 0) {
      message.warning('请先保存父步骤，再拖入子步骤')
      return
    }
    const childCount = steps.filter((s) => s.parent_id === parentId).length
    // 本地更新：移入父作用域末尾，顶层 sort 重编号
    let topSort = 0
    const next = steps
      .map((s) => (s.id === dragId ? { ...s, parent_id: parentId, sort: childCount } : s))
      .map((s) => (s.parent_id == null ? { ...s, sort: topSort++ } : s))
    setSteps(next)
    stepsRef.current = next
    setExpandedStepIds((prev) => ({ ...prev, [parentId]: true }))
    if (editingScenario && dragId > 0) {
      try {
        await scenarioApi.moveStep(editingScenario.id, dragId, { parent_id: parentId, sort: childCount })
        const orderedIds = next.filter((s) => s.parent_id == null && s.id > 0).map((s) => s.id)
        if (orderedIds.length > 0) {
          await scenarioApi.reorderSteps(editingScenario.id, orderedIds)
        }
      } catch (err) {
        message.error('移动保存失败：' + (err as Error).message)
      }
    }
  }

  // 将子步骤移出到顶层指定位置
  const moveStepToTop = async (dragId: number, topIndex: number) => {
    const dragStep = steps.find((s) => s.id === dragId)
    if (!dragStep || dragStep.parent_id == null) return
    const topLevel = steps.filter((s) => s.parent_id == null)
    const topIds = topLevel.map((s) => s.id)
    const insertAt = Math.max(0, Math.min(topIndex, topIds.length))
    topIds.splice(insertAt, 0, dragId)
    // 重建完整顺序：顶层按新顺序，子步骤跟随（被拖出的子步骤 parent_id 置空）
    const childMap = new Map<number, ScenarioStep[]>()
    steps.filter((s) => s.parent_id != null && s.id !== dragId).forEach((s) => {
      const list = childMap.get(s.parent_id!) || []
      list.push(s)
      childMap.set(s.parent_id!, list)
    })
    const next: ScenarioStep[] = []
    topIds.forEach((id) => {
      const top = id === dragId ? { ...dragStep, parent_id: null } : steps.find((s) => s.id === id)
      if (top) {
        next.push({ ...top, sort: next.length })
        ;(childMap.get(id) || []).forEach((c) => next.push(c))
      }
    })
    setSteps(next)
    stepsRef.current = next
    if (editingScenario && dragId > 0) {
      try {
        await scenarioApi.moveStep(editingScenario.id, dragId, { parent_id: null, sort: insertAt })
        const orderedIds = topIds.filter((id) => id > 0)
        if (orderedIds.length > 0) {
          await scenarioApi.reorderSteps(editingScenario.id, orderedIds)
        }
      } catch (err) {
        message.error('移动保存失败：' + (err as Error).message)
      }
    }
  }

  // 拖拽放置：inside 放入控制步骤作用域；before 顶层排序或子步骤拖出到顶层
  const handleDrop = async (e: React.DragEvent, targetStep: ScenarioStep, targetTopIndex: number, mode?: 'before' | 'inside') => {
    e.preventDefault()
    const dragId = dragStepIdRef.current
    setDragOverIndex(null)
    setDropIndicator(null)
    dragStepIdRef.current = null
    if (dragId === null) return
    const dropMode = mode || dropModeRef.current
    if (dropMode === 'inside') {
      await moveStepIntoScope(dragId, targetStep.id)
      return
    }
    const dragStep = steps.find((s) => s.id === dragId)
    if (!dragStep) return
    // 子步骤拖出到顶层列表
    if (dragStep.parent_id != null) {
      await moveStepToTop(dragId, targetTopIndex)
      return
    }
    const topLevel = steps.filter((s) => !s.parent_id)
    const fromIndex = topLevel.findIndex((s) => s.id === dragId)
    // 拖到自己身上不处理
    if (fromIndex === -1 || fromIndex === targetTopIndex) return
    // 计算目标插入位置（splice 语义：先移除源元素，再在目标位置插入）
    // - 向下拖动（fromIndex < dropIndex）：移除源元素后 dropIndex 位置元素前移，
    //   插入到 dropIndex 即放到目标元素之后（实现调换）
    // - 向上拖动（fromIndex > dropIndex）：直接插入到 dropIndex 位置（目标元素之前）
    let targetIndex = targetTopIndex
    const topIds = topLevel.map((s) => s.id)
    const [moved] = topIds.splice(fromIndex, 1)
    // 移除元素后修正边界
    if (targetIndex > topIds.length) targetIndex = topIds.length
    if (targetIndex < 0) targetIndex = 0
    topIds.splice(targetIndex, 0, moved)
    await reorderTopSteps(topIds)
  }

  const handleDragEnd = () => {
    dragStepIdRef.current = null
    setDragOverIndex(null)
    setDropIndicator(null)
    dropModeRef.current = 'before'
  }

  // 拖放到列表底部区域：子步骤追加到顶层末尾（顶层步骤已在末位时无操作）
  const handleDropToBottom = async (e: React.DragEvent) => {
    e.preventDefault()
    const dragId = dragStepIdRef.current
    setDragOverIndex(null)
    setDropIndicator(null)
    dragStepIdRef.current = null
    if (dragId === null) return
    const dragStep = steps.find((s) => s.id === dragId)
    if (!dragStep) return
    const topLevel = steps.filter((s) => !s.parent_id)
    if (dragStep.parent_id != null) {
      await moveStepToTop(dragId, topLevel.length)
    }
  }

  // 上移/下移步骤（仅顶层步骤）
  const handleMoveStep = async (stepId: number, direction: 'up' | 'down') => {
    const topLevel = steps.filter((s) => !s.parent_id)
    const currentIndex = topLevel.findIndex((s) => s.id === stepId)
    if (currentIndex === -1) return
    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1
    if (targetIndex < 0 || targetIndex >= topLevel.length) return
    const topIds = topLevel.map((s) => s.id)
    ;[topIds[currentIndex], topIds[targetIndex]] = [topIds[targetIndex], topIds[currentIndex]]
    await reorderTopSteps(topIds)
  }

  // 引用 steps 最新值，便于异步回调读取
  const stepsRef = useRef<ScenarioStep[]>([])
  useEffect(() => { stepsRef.current = steps }, [steps])

  // 列表直接执行中：正在执行的场景 ID 集合
  const [executingIds, setExecutingIds] = useState<Record<number, boolean>>({})

  // 运行场景：按步骤顺序执行，提取的变量按顺序累积供后续步骤使用
  // scenarioOverride/stepsOverride：场景列表"执行"入口在抽屉步骤加载完成后自动运行时传入
  const handleRunScenario = async (scenarioOverride?: Scenario, stepsOverride?: ScenarioStep[]) => {
    const targetScenario = scenarioOverride || editingScenario
    if (!targetScenario) return
    const runSteps = stepsOverride || steps
    if (runSteps.length === 0) {
      message.warning('当前场景没有步骤，请先添加步骤')
      return
    }
    const runStartAt = Date.now()
    setScenarioRunning(true)
    // 重置变量池和步骤状态
    scenarioVariablesRef.current.clear()
    setStepRunResults({})
    setStepRunAssertionResults({})
    setStepRunExtractResults({})
    const initialStatus: Record<number, { status: 'pending' | 'running' | 'success' | 'failed'; variables?: Record<string, string>; message?: string }> = {}
    runSteps.forEach((s) => { initialStatus[s.id] = { status: 'pending' } })
    setStepRunStatus(initialStatus)

    const projectId = currentProject?.id || 0
    let successCount = 0
    let failedCount = 0
    let errorCount = 0
    let aborted = false
    let assertionPassedCount = 0
    let assertionTotalCount = 0
    let extractSuccessCount = 0
    // 最近一次 HTTP 步骤的响应，供断言控制步骤使用
    let lastResponse: { status: number; body: string; headers: Record<string, string>; elapsed_ms: number } | null = null
    let extractTotalCount = 0
    // 步骤执行记录（供测试报告步骤明细使用）：耗时 + 最终状态/消息/结果详情
    const stepDurations = new Map<number, number>()
    const stepResultRecords = new Map<number, { status: 'success' | 'failed' | 'error' | 'skipped'; message?: string; detail?: any }>()
    // 标记步骤最终执行状态：写入 UI 状态并记录到报告明细
    const markStatus = (id: number, status: 'success' | 'failed' | 'error' | 'skipped', message?: string, detail?: any) => {
      stepResultRecords.set(id, { status, message, detail })
      setStepRunStatus((prev) => ({ ...prev, [id]: { status, message } }))
    }

    // 组装作用域：条件/循环/断言控制步骤内的子步骤
    const childMap = new Map<number, ScenarioStep[]>()
    runSteps.filter((s) => s.parent_id).forEach((s) => {
      const list = childMap.get(s.parent_id!) || []
      list.push(s)
      childMap.set(s.parent_id!, list)
    })
    const topLevelSteps = runSteps.filter((s) => !s.parent_id)
    // 子步骤批量标记跳过
    const skipChildren = (children: ScenarioStep[], reason: string) => {
      children.forEach((c) => markStatus(c.id, 'skipped', reason))
    }

    // 单步骤执行（HTTP 请求/等待/条件/断言/数据库/脚本），返回该步骤是否失败
    const runOneStep = async (step: ScenarioStep): Promise<boolean> => {
      if (aborted) return true
      const stepStartAt = Date.now()
      // 自动选中当前执行的步骤，运行结果将展示在右侧步骤详情中（与调试一致）
      await handleSelectStep(step)
      setStepRunStatus((prev) => ({ ...prev, [step.id]: { status: 'running' } }))
      try {
        const stepTypeStr = typeof step.step_type === 'string' ? step.step_type : { 1: 'http', 2: 'wait', 3: 'condition', 4: 'loop', 5: 'assertion', 6: 'db', 7: 'script' }[step.step_type as number] || 'http'
        if (stepTypeStr !== 'http') {
          const detail: any = await scenarioApi.getStepDetail(targetScenario.id, step.id).catch(() => null)
          const variables = scenarioVariablesRef.current

          if (stepTypeStr === 'wait') {
            // 等待控制：固定时长等待，或轮询条件直到满足/超时
            const duration = Number(detail?.duration_ms) || 0
            const waitCond = (detail?.condition || '').trim()
            if (waitCond) {
              const maxWait = duration > 0 ? duration : 30000
              const startAt = Date.now()
              let satisfied = false
              while (Date.now() - startAt < maxWait) {
                if (evaluateConditionExpression(waitCond, variables)) {
                  satisfied = true
                  break
                }
                await sleep(500)
              }
              if (satisfied) {
                markStatus(step.id, 'success', `等待条件满足，耗时 ${Date.now() - startAt}ms`)
                successCount++
                return false
              } else {
                markStatus(step.id, 'failed', `等待条件在 ${maxWait}ms 内未满足，运行中止`)
                failedCount++
                aborted = true
                return true
              }
            } else {
              await sleep(duration)
              markStatus(step.id, 'success', `已等待 ${duration}ms`)
              successCount++
              return false
            }
          } else if (stepTypeStr === 'assertion') {
            // 断言控制：对上一个 HTTP 步骤的响应执行断言规则
            let assertRules: any[] = []
            try {
              const parsed = JSON.parse(detail?.rules || '[]')
              if (Array.isArray(parsed)) assertRules = parsed
            } catch {
              /* 规则解析失败按空规则处理 */
            }
            if (!lastResponse) {
              markStatus(step.id, 'failed', '断言失败：前面没有已执行的 HTTP 响应，运行中止')
              failedCount++
              aborted = true
              return true
            } else {
              const results = evaluateStepAssertions(assertRules, lastResponse)
              setStepRunAssertionResults((prev) => ({ ...prev, [step.id]: results }))
              assertionTotalCount += results.length
              const passedCount = results.filter((r) => r.passed).length
              assertionPassedCount += passedCount
              if (results.every((r) => r.passed)) {
                markStatus(step.id, 'success', `断言 ${passedCount}/${results.length} 通过`, { assertions: results })
                successCount++
                return false
              } else {
                markStatus(step.id, 'failed', `断言 ${passedCount}/${results.length} 通过，运行中止`, { assertions: results })
                failedCount++
                aborted = true
                return true
              }
            }
          } else if (stepTypeStr === 'condition') {
            // 条件控制：对表达式求值，不满足则中止后续步骤
            const expr = (detail?.expression || '').trim()
            if (!expr) {
              markStatus(step.id, 'failed', '条件表达式为空，运行中止')
              failedCount++
              aborted = true
              return true
            } else if (evaluateConditionExpression(expr, variables)) {
              markStatus(step.id, 'success', '条件满足')
              successCount++
              return false
            } else {
              markStatus(step.id, 'failed', `条件不满足：${applyVariables(expr, variables)}，运行中止`)
              failedCount++
              aborted = true
              return true
            }
          } else {
            // 循环/数据库/脚本暂不支持自动执行，计为跳过
            markStatus(step.id, 'skipped', '该步骤类型暂不支持自动执行，已跳过')
            return false
          }
        }

        // 加载步骤详情（优先从缓存读取）
        let detail: any = null
        const cached = stepDetailCacheRef.current.get(step.id)
        if (cached) {
          detail = cached.detail
        } else {
          detail = await scenarioApi.getStepDetail(targetScenario.id, step.id)
        }
        if (!detail || !detail.api_id) {
          markStatus(step.id, 'failed', '步骤详情缺失或未关联接口')
          failedCount++
          return true
        }

        // 加载接口定义
        let api: ApiDefinition | null = cached?.api || null
        if (!api) {
          api = await apiDefApi.getApiDefinition(detail.api_id, projectId)
        }
        if (!api) {
          markStatus(step.id, 'failed', '接口定义不存在')
          failedCount++
          return true
        }

        // 变量替换
        const variables = scenarioVariablesRef.current
        const envUrl = applyVariables(api.host || '', variables)
        let path = applyVariables(api.path || '', variables)
        const restEntries = jsonToKvArray(api.rest_params || '[]')
        restEntries.filter((e: any) => e.key?.trim()).forEach((e: any) => {
          const v = applyVariables(e.value || '', variables)
          path = path.replace(`{${e.key.trim()}}`, v)
        })
        const fullUrl = envUrl ? envUrl.replace(/\/+$/, '') + '/' + path.replace(/^\/+/, '') : path

        const headers: Record<string, string> = {}
        jsonToKvArray(api.headers || '[]').filter((e: any) => e.key?.trim()).forEach((e: any) => {
          headers[e.key.trim()] = applyVariables(e.value || '', variables)
        })
        const queryParams: Record<string, string> = {}
        jsonToKvArray(api.query_params || '[]').filter((e: any) => e.key?.trim()).forEach((e: any) => {
          queryParams[e.key.trim()] = applyVariables(e.value || '', variables)
        })

        let body: string | null = null
        const effectiveBodyType = api.body_type || 'none'
        const bodyFormEntries = loadBodyFormEntries(api.body || '[]', effectiveBodyType)
        const bodyValue = api.body || ''
        if ((effectiveBodyType === 'form-data' || effectiveBodyType === 'x-www-form-urlencoded') && bodyFormEntries.length) {
          const formData: Record<string, any> = {}
          bodyFormEntries.forEach((entry: any) => {
            if (entry.key) {
              formData[entry.key] = applyVariables(entry.remark || '', variables)
            }
          })
          body = JSON.stringify(formData)
        } else if (bodyValue.trim()) {
          body = applyVariables(bodyValue, variables)
        }

        const res = await post<{ status: number; body: string; headers: Record<string, string>; elapsed_ms: number }>('/api-definitions/debug', {
          method: api.method,
          url: fullUrl,
          headers,
          query_params: queryParams,
          body_type: effectiveBodyType,
          body,
        })

        // 执行参数提取，将成功提取的变量存入变量池
        let extractRules: { id: string; name: string; method: string; expression: string; description: string }[] = []
        try {
          const parsed = JSON.parse(detail.extract_rules || '[]')
          if (Array.isArray(parsed)) {
            extractRules = parsed.map((r: any, idx: number) => ({
              id: r.id || `extract_${idx}`,
              name: r.name || '',
              method: r.method || 'jsonpath',
              expression: r.expression || '',
              description: r.description || '',
            }))
          }
        } catch {
          // ignore
        }
        const extractedVars: Record<string, string> = {}
        const extractResults = extractRules.map((rule) => {
          const extracted = extractValue(rule, res)
          if (extracted.success && rule.name) {
            scenarioVariablesRef.current.set(rule.name, extracted.value)
            extractedVars[rule.name] = extracted.value
          }
          return { id: rule.id, name: rule.name, success: extracted.success, value: extracted.value, message: extracted.message }
        })

        // 运行结果展示在右侧步骤详情响应区域（与调试一致），并持久化供切换步骤后查看
        setStepRunResults((prev) => ({ ...prev, [step.id]: res }))
        lastResponse = res
        setStepResponseResult(res)
        setStepResponseTab('body')
        setStepExtractResults(extractResults)
        setStepRunExtractResults((prev) => ({ ...prev, [step.id]: extractResults }))

        // 执行断言规则（与调试一致），并持久化供切换步骤后查看
        let runAssertionRules: { id: string; type: string; operator: string; expected: string; key: string; expression: string }[] = []
        try {
          const parsed = JSON.parse(api.assertions || '[]')
          if (Array.isArray(parsed)) runAssertionRules = parsed
        } catch {
          // ignore
        }
        const assertionResults = evaluateStepAssertions(runAssertionRules, res)
        setStepRunAssertionResults((prev) => ({ ...prev, [step.id]: assertionResults }))
        assertionTotalCount += assertionResults.length
        assertionPassedCount += assertionResults.filter((r) => r.passed).length
        extractTotalCount += extractResults.length
        extractSuccessCount += extractResults.filter((r) => r.success).length

        const httpStatus: 'success' | 'failed' = res.status < 400 ? 'success' : 'failed'
        markStatus(step.id, httpStatus, `HTTP ${res.status}, ${res.elapsed_ms}ms`, {
          response: {
            status: res.status,
            elapsed_ms: res.elapsed_ms,
            body: res.body,
            headers: res.headers,
          },
          assertions: assertionResults,
          extracts: extractResults,
        })
        if (res.status < 400) {
          successCount++
          return false
        } else {
          failedCount++
          return true
        }
      } catch (e) {
        markStatus(step.id, 'error', `执行异常: ${(e as Error).message}`)
        errorCount++
        // 发生异常时中止后续步骤
        aborted = true
        return true
      } finally {
        stepDurations.set(step.id, Date.now() - stepStartAt)
      }
    }

    // 主循环：顶层步骤线性执行；条件/循环/断言控制步骤作为作用域，
    // 内部子步骤受控制结果影响（不通过时仅跳过作用域内步骤，不阻塞外部后续步骤）
    for (const step of topLevelSteps) {
      if (aborted) break
      const stepTypeStr = typeof step.step_type === 'string' ? step.step_type : { 1: 'http', 2: 'wait', 3: 'condition', 4: 'loop', 5: 'assertion', 6: 'db', 7: 'script' }[step.step_type as number] || 'http'
      if (stepTypeStr === 'condition' || stepTypeStr === 'loop' || stepTypeStr === 'assertion') {
        await handleSelectStep(step)
        setStepRunStatus((prev) => ({ ...prev, [step.id]: { status: 'running' } }))
        const children = (childMap.get(step.id) || []).sort((a, b) => a.sort - b.sort)
        const ctrlStartAt = Date.now()
        try {
          const detail: any = await scenarioApi.getStepDetail(targetScenario.id, step.id).catch(() => null)
          const variables = scenarioVariablesRef.current
          if (stepTypeStr === 'condition') {
            // 条件控制：满足执行内部步骤；不满足仅跳过内部步骤，继续外部后续步骤
            const expr = (detail?.expression || '').trim()
            if (!expr) {
              markStatus(step.id, 'success', `条件表达式为空，已跳过内部 ${children.length} 个步骤`)
              skipChildren(children, '条件表达式为空，已跳过')
              successCount++
            } else if (evaluateConditionExpression(expr, variables)) {
              markStatus(step.id, 'success', '条件满足')
              successCount++
              for (const child of children) {
                if (aborted) break
                await runOneStep(child)
              }
            } else {
              markStatus(step.id, 'success', `条件不满足：${applyVariables(expr, variables)}，已跳过内部 ${children.length} 个步骤`)
              skipChildren(children, '条件不满足，已跳过')
              successCount++
            }
          } else if (stepTypeStr === 'loop') {
            // 循环控制：count 按固定次数执行（一轮失败即中止）；其他类型按最大重试次数重试（一轮成功即结束）
            const loopType = detail?.loop_type || 'count'
            if (children.length === 0) {
              markStatus(step.id, 'success', '循环内没有步骤')
              successCount++
            } else {
              const maxRounds = loopType === 'count' ? Math.max(1, Number(detail?.times) || 1) : Math.max(1, Number(detail?.max_retries) || 1)
              let completed = 0
              let loopFailed = false
              for (let round = 0; round < maxRounds && !aborted; round++) {
                let roundFailed = false
                for (const child of children) {
                  if (aborted) break
                  const failed = await runOneStep(child)
                  if (failed) {
                    roundFailed = true
                    break
                  }
                }
                if (roundFailed) {
                  if (loopType === 'count') {
                    loopFailed = true
                    break
                  }
                } else {
                  completed = round + 1
                  if (loopType !== 'count') break
                }
              }
              if (loopFailed || aborted) {
                markStatus(step.id, 'failed', aborted ? '循环中步骤执行异常，运行中止' : `第 ${completed + 1} 轮循环中步骤失败，提前结束`)
                failedCount++
              } else {
                markStatus(step.id, 'success', `循环执行完成，共 ${completed} 轮`)
                successCount++
              }
            }
          } else {
            // 断言控制：对上一个 HTTP 步骤的响应执行断言；通过执行内部步骤，失败仅跳过内部步骤
            let assertRules: any[] = []
            try {
              const parsed = JSON.parse(detail?.rules || '[]')
              if (Array.isArray(parsed)) assertRules = parsed
            } catch {
              /* 规则解析失败按空规则处理 */
            }
            if (!lastResponse) {
              markStatus(step.id, 'failed', '断言失败：前面没有已执行的 HTTP 响应')
              failedCount++
              skipChildren(children, '断言未通过，已跳过')
            } else {
              const results = evaluateStepAssertions(assertRules, lastResponse)
              setStepRunAssertionResults((prev) => ({ ...prev, [step.id]: results }))
              assertionTotalCount += results.length
              const passedCount = results.filter((r) => r.passed).length
              assertionPassedCount += passedCount
              if (results.every((r) => r.passed)) {
                markStatus(step.id, 'success', `断言 ${passedCount}/${results.length} 通过`, { assertions: results })
                successCount++
                for (const child of children) {
                  if (aborted) break
                  await runOneStep(child)
                }
              } else {
                markStatus(step.id, 'failed', `断言 ${passedCount}/${results.length} 通过，已跳过内部 ${children.length} 个步骤`, { assertions: results })
                failedCount++
                skipChildren(children, '断言未通过，已跳过')
              }
            }
          }
        } catch (e) {
          markStatus(step.id, 'error', `控制步骤执行异常: ${(e as Error).message}`)
          errorCount++
          skipChildren(children, '控制步骤异常，已跳过')
        } finally {
          stepDurations.set(step.id, Date.now() - ctrlStartAt)
        }
        continue
      }
      await runOneStep(step)
    }

    setScenarioRunning(false)
    // 汇总断言、参数提取统计（仅在有规则时展示）
    const stats: string[] = []
    if (assertionTotalCount > 0) stats.push(`断言通过 ${assertionPassedCount}/${assertionTotalCount}`)
    if (extractTotalCount > 0) stats.push(`参数提取成功 ${extractSuccessCount}/${extractTotalCount}`)
    const baseText = aborted
      ? `场景运行中止：成功 ${successCount} 个，失败 ${failedCount + errorCount} 个`
      : failedCount + errorCount > 0
        ? `场景运行完成：成功 ${successCount} 个，失败 ${failedCount + errorCount} 个`
        : `场景运行完成：全部 ${successCount} 个步骤执行成功`
    const summary = stats.length > 0 ? `${baseText}，${stats.join('，')}` : baseText
    if (aborted) {
      message.error(summary)
    } else if (failedCount + errorCount > 0) {
      message.warning(summary)
    } else {
      message.success(summary)
    }

    // 运行完成，上报生成测试报告（含步骤执行记录，失败不影响运行提示）
    const runResult = aborted || failedCount > 0 || errorCount > 0 ? 2 : 1
    const stepTypeStrToNum: Record<string, number> = { http: 1, wait: 2, condition: 3, loop: 4, assertion: 5, db: 6, script: 7 }
    // 按实际执行顺序组装：顶层步骤 → 其作用域内子步骤
    const orderedRunSteps: { step: ScenarioStep; depth: number }[] = []
    topLevelSteps.forEach((s) => {
      orderedRunSteps.push({ step: s, depth: 0 })
      ;(childMap.get(s.id) || []).sort((a, b) => a.sort - b.sort).forEach((c) => orderedRunSteps.push({ step: c, depth: 1 }))
    })
    const reportSteps = orderedRunSteps.map(({ step: s, depth }) => {
      const rec = stepResultRecords.get(s.id)
      const st = rec?.status ?? 'skipped'
      return {
        step_name: s.name,
        step_type: typeof s.step_type === 'number' ? s.step_type : stepTypeStrToNum[s.step_type] || 1,
        status: st === 'success' ? 1 : st === 'failed' ? 2 : st === 'error' ? 3 : 4,
        duration_ms: stepDurations.get(s.id) || 0,
        depth,
        result: JSON.stringify({ message: rec?.message ?? '', ...(rec?.detail ? rec.detail : {}) }),
      }
    })
    try {
      await reportApi.createReport({
        name: `${targetScenario.name}-执行报告 ${dayjs().format('MM-DD HH:mm:ss')}`,
        task_type: 1, // 接口场景测试
        run_result: runResult,
        run_mode: 1, // 同步
        total_steps: runSteps.length,
        success_steps: successCount,
        duration_ms: Date.now() - runStartAt,
        scenario_id: targetScenario.id,
        project_id: targetScenario.project_id ?? projectId,
        executor_name: user?.nickname || user?.username || '',
        summary,
        steps: reportSteps,
      })
      // 报告落库时后端已回写场景最后执行状态/时间，这里同步更新列表行，免整表刷新
      setScenarios((prev) => prev.map((s) => s.id === targetScenario.id
        ? {
            ...s,
            last_execution_status: runResult === 1 ? 'passed' : 'failed',
            last_execution_time: dayjs().format('YYYY-MM-DD HH:mm:ss'),
          }
        : s))
    } catch {
      /* 报告生成失败不阻断 */
    }
  }

  const { currentProject } = useProjectStore()
  const { user } = useAuthStore()
  const projectId = currentProject?.id || 0

  const loadApiList = async (page: number) => {
    if (!projectId) return
    setApiListLoading(true)
    try {
      const res = await apiDefApi.listApiDefinitions({
        project_id: projectId,
        page,
        page_size: 10,
      })
      setApiList(res.data || [])
      setApiListTotal(res.total)
      setApiListPage(page)
    } catch {
      setApiList([])
    } finally {
      setApiListLoading(false)
    }
  }

  // 运行环境选项：来自当前项目的环境列表（编辑时若原环境已不存在则保留显示）
  const envOptions = (() => {
    const names = envList.map((e) => e.url).filter(Boolean)
    const current = editingScenario?.environment
    if (current && !names.includes(current)) names.unshift(current)
    return names.map((n) => ({ value: n, label: n }))
  })()

  // ============ 数据加载 ============

  const loadModules = useCallback(async () => {
    try {
      const data = await scenarioApi.getModuleTree(projectId)
      setModules(data)
    } catch {
      // 忽略
    }
  }, [projectId])

  const loadScenarios = useCallback(async () => {
    if (!projectId) return
    setLoading(true)
    try {
      const res = await scenarioApi.listScenarios({
        project_id: projectId,
        module_id: selectedModule || undefined,
        page,
        page_size: pageSize,
        keyword: searchKeyword || undefined,
      })
      setScenarios(res.data)
      setTotal(res.total)
    } finally {
      setLoading(false)
    }
  }, [projectId, selectedModule, page, pageSize, searchKeyword])

  useEffect(() => {
    loadModules()
  }, [loadModules])

  // 加载当前项目下的环境列表（用于运行环境下拉）
  useEffect(() => {
    if (!projectId) return
    environmentApi
      .listEnvironments({ project_id: projectId, page_size: 100 })
      .then((res) => setEnvList(res.data || []))
      .catch(() => setEnvList([]))
  }, [projectId])

  useEffect(() => {
    loadScenarios()
  }, [loadScenarios])

  const handleSearch = () => {
    setSearchKeyword(keyword.trim())
    setPage(1)
  }

  // ============ 模块树 ============

  const treeData: TreeDataNode[] = (function convert(list: ScenarioModule[]): TreeDataNode[] {
    return list.map((m) => ({
      key: m.id,
      title: (
        <Space>
          <FolderOutlined style={{ color: 'var(--au-primary)' }} />
          <span>{m.name}</span>
        </Space>
      ),
      children: m.children ? convert(m.children) : undefined,
    }))
  })(modules)

  const moduleSelectTreeData = (function convert(list: ScenarioModule[]): any[] {
    return list.map((m) => ({
      value: m.id,
      title: m.name,
      children: m.children ? convert(m.children) : undefined,
    }))
  })(modules)

  const openCreateModuleModal = (parentId: number | null = null) => {
    setEditingModule(null)
    moduleForm.resetFields()
    moduleForm.setFieldValue('parent_id', parentId)
    setModuleModalOpen(true)
  }

  const openEditModuleModal = (mod: ScenarioModule) => {
    setEditingModule(mod)
    moduleForm.setFieldsValue({ name: mod.name })
    setModuleModalOpen(true)
  }

  const handleModuleSubmit = async () => {
    try {
      const values = await moduleForm.validateFields()
      if (editingModule) {
        await scenarioApi.updateModule(editingModule.id, { name: values.name })
        message.success('模块名称更新成功')
      } else {
        await scenarioApi.createModule({
          project_id: projectId,
          parent_id: values.parent_id || null,
          name: values.name,
          sort: 0,
        })
        message.success('模块创建成功')
      }
      setModuleModalOpen(false)
      moduleForm.resetFields()
      setEditingModule(null)
      loadModules()
    } catch {
      // 表单校验错误
    }
  }

  const handleDeleteModule = async (mod: ScenarioModule) => {
    try {
      await scenarioApi.deleteModule(mod.id)
      message.success('模块已删除')
      if (selectedModule === mod.id) {
        setSelectedModule(null)
      }
      loadModules()
    } catch {
      // 错误已在拦截器处理
    }
  }

  const buildContextMenuItems = (mod: ScenarioModule): MenuProps['items'] => {
    return [
      {
        key: 'edit',
        label: '修改名称',
        onClick: () => openEditModuleModal(mod),
      },
      {
        key: 'delete',
        label: '删除',
        danger: true,
        onClick: () => {
          Modal.confirm({
            title: '确认删除',
            content: `确定要删除模块「${mod.name}」吗？其下所有子模块将被一起删除。`,
            okText: '确定删除',
            okType: 'danger',
            cancelText: '取消',
            wrapClassName: 'aurora-modal-wrap',
            className: 'aurora-modal',
            onOk: () => handleDeleteModule(mod),
          })
        },
      },
    ]
  }

  const handleTreeRightClick = (info: { event: React.MouseEvent; node: { key: React.Key } }) => {
    const { node } = info
    const key = Number(node.key)
    const findModule = (list: ScenarioModule[]): ScenarioModule | null => {
      for (const m of list) {
        if (m.id === key) return m
        if (m.children) {
          const found = findModule(m.children)
          if (found) return found
        }
      }
      return null
    }
    const mod = findModule(modules)
    if (mod) {
      setContextMenuModule(mod)
    }
  }

  // ============ 场景 CRUD ============

  const handleCreate = () => {
    setEditingScenario(null)
    scenarioForm.resetFields()
    scenarioForm.setFieldsValue({
      name: '',
      description: '',
      environment: envList[0]?.url || undefined,
      module_id: selectedModule || undefined,
    })
    setSteps([])
    setSelectedStepIdSync(null)
    setStepDetailMode('step_form')
    setSelectedImportApi(null)
    setApiImportModalOpen(false)
    stepForm.resetFields()
    setDrawerOpen(true)
  }

  const handleEdit = async (record: Scenario) => {
    setEditingScenario(record)
    scenarioForm.setFieldsValue({
      name: record.name,
      description: record.description,
      environment: record.environment,
      module_id: record.module_id || undefined,
    })
    setSelectedStepIdSync(null)
    setStepDetailMode('step_form')
    setSelectedImportApi(null)
    setApiImportModalOpen(false)
    stepForm.resetFields()
    // 加载步骤
    setStepsLoading(true)
    let stepList: ScenarioStep[] = []
    try {
      stepList = await scenarioApi.listSteps(record.id)
      setSteps(stepList)
      // 预取各 HTTP 步骤详情中的参数提取规则，让「引用变量」无需运行/逐个点开即可列出已配置的变量
      const httpSteps = stepList.filter((s) => s.step_type === 'http')
      if (httpSteps.length) {
        await Promise.all(
          httpSteps.map((s) =>
            scenarioApi
              .getStepDetail(record.id, s.id)
              .then((d: any) => {
                extractRulesCacheRef.current.set(s.id, d?.extract_rules || '[]')
              })
              .catch(() => {}),
          ),
        )
      }
    } catch {
      setSteps([])
    } finally {
      setStepsLoading(false)
    }
    setDrawerOpen(true)
  }

  const handleDelete = async (id: number) => {
    try {
      await scenarioApi.deleteScenario(id)
      message.success('删除成功')
      if (editingScenario?.id === id) {
        setDrawerOpen(false)
      }
      loadScenarios()
    } catch {
      // 错误已在拦截器处理
    }
  }

  // 持久化单个临时步骤（基于快照数据），返回创建的真实步骤
  const persistTempStep = async (
    scenarioId: number,
    stepId: number,
    sort: number,
    snapshots: Record<number, TempStepSnapshot>,
    parentId?: number | null,
  ) => {
    const snapshot = snapshots[stepId]
    if (!snapshot) return null
    const stepValues = snapshot.formValues
    const stepTypeNumber =
      typeof stepValues.step_type === 'string'
        ? { http: 1, wait: 2, condition: 3, loop: 4, assertion: 5, db: 6, script: 7 }[stepValues.step_type as string] || 1
        : stepValues.step_type
    const stepTypeStr =
      typeof stepValues.step_type === 'string'
        ? stepValues.step_type
        : ({ 1: 'http', 2: 'wait', 3: 'condition', 4: 'loop', 5: 'assertion', 6: 'db', 7: 'script' } as Record<number, string>)[stepValues.step_type as number] || 'http'

    const createdStep = await scenarioApi.createStep(scenarioId, {
      name: stepValues.name,
      step_type: stepTypeNumber,
      sort,
      parent_id: parentId ?? null,
    })
    const realStepId = createdStep.id

    if (snapshot.detailMode === 'api_detail' || snapshot.detailMode === 'new_request') {
      const effectiveBodyType = stepValues.body_type || snapshot.bodyType || 'none'
      const bodyFormEntries = stepValues.bodyFormEntries || []
      const bodyValue = stepValues.body || ''
      let finalBody = bodyValue
      if ((effectiveBodyType === 'form-data' || effectiveBodyType === 'x-www-form-urlencoded') && bodyFormEntries.length) {
        finalBody = JSON.stringify(
          bodyFormEntries
            .filter((e: any) => e.key && e.key.trim())
            .map((e: any) => ({ key: e.key, type: e.value || 'string', value: e.remark || '' })),
        )
      }

      const apiPayload = {
        project_id: projectId,
        name: stepValues.name,
        method: stepValues.method,
        path: stepValues.path,
        host: stepValues.host || '',
        headers: kvArrayToJson(stepValues.headerEntries || []),
        query_params: kvArrayToJson(stepValues.paramEntries || []),
        rest_params: kvArrayToJson(stepValues.restEntries || []),
        body_type: effectiveBodyType,
        body: finalBody,
        description: stepValues.description || '',
        assertions: JSON.stringify(snapshot.assertionRules),
      }

      let apiId = snapshot.api?.id
      if (snapshot.detailMode === 'api_detail' && apiId) {
        await apiDefApi.updateApiDefinition(apiId, apiPayload, projectId)
      } else if (snapshot.detailMode === 'new_request') {
        if (apiId) {
          await apiDefApi.updateApiDefinition(apiId, apiPayload, projectId)
        } else {
          const created = await apiDefApi.createApiDefinition({
            ...apiPayload,
            creator_id: user?.id || 1,
          })
          apiId = created.id
        }
      }

      await scenarioApi.createStepDetail(scenarioId, realStepId, {
        api_id: apiId,
        pre_process: '',
        post_process: '',
        extract_rules: JSON.stringify(snapshot.extractRules || []),
      })
    } else if (snapshot.detailMode === 'step_form') {
      // name 存于步骤表（由 createStep 保存），详情表无 name 列
      const detailData: Record<string, any> = {}
      if (stepTypeStr === 'wait') {
        detailData.duration_ms = Number(stepValues.duration_ms) || 0
        detailData.condition = stepValues.condition || ''
      } else if (stepTypeStr === 'condition') {
        detailData.expression = stepValues.expression || ''
        detailData.expected_value = stepValues.expected_value || ''
      } else if (stepTypeStr === 'loop') {
        detailData.loop_type = stepValues.loop_type || 'count'
        detailData.times = Number(stepValues.times) || 1
        detailData.max_retries = Number(stepValues.max_retries) || 0
      } else if (stepTypeStr === 'assertion') {
        detailData.rules = JSON.stringify(snapshot.assertionRules)
      } else if (stepTypeStr === 'db') {
        detailData.datasource_id = stepValues.datasource_id
        detailData.sql_type = stepValues.sql_type || 'select'
        detailData.sql = stepValues.sql || ''
        detailData.expected = stepValues.expected || ''
      } else if (stepTypeStr === 'script') {
        detailData.language = stepValues.language || 'javascript'
        detailData.content = stepValues.content || ''
        detailData.timeout = Number(stepValues.timeout) || 60
      }
      await scenarioApi.createStepDetail(scenarioId, realStepId, detailData)
    }
    return createdStep
  }

  /** 持久化已存在步骤（id>0）的编辑内容：供「保存步骤」与「保存场景」共用（内部含表单校验） */
  const persistExistingStep = async (stepId: number) => {
    if (!editingScenario || !stepId) return
    const stepValues = await readStepFormValues()

    const stepTypeNumber =
      typeof stepValues.step_type === 'string'
        ? { http: 1, wait: 2, condition: 3, loop: 4, assertion: 5, db: 6, script: 7 }[stepValues.step_type as string] || 1
        : stepValues.step_type
    // 归一化为字符串（新增步骤时表单写入的是数字），用于详情字段分支判断
    const stepTypeStr =
      typeof stepValues.step_type === 'string'
        ? stepValues.step_type
        : ({ 1: 'http', 2: 'wait', 3: 'condition', 4: 'loop', 5: 'assertion', 6: 'db', 7: 'script' } as Record<number, string>)[stepValues.step_type as number] || 'http'

    await scenarioApi.updateStep(editingScenario.id, stepId, {
      name: stepValues.name,
      step_type: stepTypeNumber,
    })

    if (stepDetailMode === 'api_detail' || stepDetailMode === 'new_request') {
      const effectiveBodyType = stepValues.body_type || stepBodyType || 'none'
      const bodyFormEntries = stepValues.bodyFormEntries || []
      const bodyValue = stepValues.body || ''
      let finalBody = bodyValue
      if ((effectiveBodyType === 'form-data' || effectiveBodyType === 'x-www-form-urlencoded') && bodyFormEntries.length) {
        finalBody = JSON.stringify(
          bodyFormEntries
            .filter((e: any) => e.key && e.key.trim())
            .map((e: any) => ({ key: e.key, type: e.value || 'string', value: e.remark || '' })),
        )
      }

      const apiPayload = {
        project_id: projectId,
        name: stepValues.name,
        method: stepValues.method,
        path: stepValues.path,
        host: stepValues.host || '',
        headers: kvArrayToJson(stepValues.headerEntries || []),
        query_params: kvArrayToJson(stepValues.paramEntries || []),
        rest_params: kvArrayToJson(stepValues.restEntries || []),
        body_type: effectiveBodyType,
        body: finalBody,
        description: stepValues.description || '',
        assertions: JSON.stringify(stepAssertionRules),
      }

      let apiId = selectedImportApi?.id
      if (stepDetailMode === 'api_detail' && apiId) {
        await apiDefApi.updateApiDefinition(apiId, apiPayload, projectId)
      } else if (stepDetailMode === 'new_request') {
        if (apiId) {
          await apiDefApi.updateApiDefinition(apiId, apiPayload, projectId)
        } else {
          const created = await apiDefApi.createApiDefinition({
            ...apiPayload,
            creator_id: user?.id || 1,
          })
          apiId = created.id
        }
      }

      extractRulesCacheRef.current.set(stepId, JSON.stringify(stepExtractRules))
      await scenarioApi.updateStepDetail(editingScenario.id, stepId, {
        api_id: apiId,
        pre_process: '',
        post_process: '',
        extract_rules: JSON.stringify(stepExtractRules),
      })
    } else if (stepDetailMode === 'step_form') {
      // name 存于步骤表（由 updateStep 保存），详情表无 name 列
      const detailData: Record<string, any> = {}
      if (stepTypeStr === 'wait') {
        detailData.duration_ms = Number(stepValues.duration_ms) || 0
        detailData.condition = stepValues.condition || ''
      } else if (stepTypeStr === 'condition') {
        detailData.expression = stepValues.expression || ''
        detailData.expected_value = stepValues.expected_value || ''
      } else if (stepTypeStr === 'loop') {
        detailData.loop_type = stepValues.loop_type || 'count'
        detailData.times = Number(stepValues.times) || 1
        detailData.max_retries = Number(stepValues.max_retries) || 0
      } else if (stepTypeStr === 'assertion') {
        detailData.rules = JSON.stringify(stepAssertionRules)
      } else if (stepTypeStr === 'db') {
        detailData.datasource_id = stepValues.datasource_id
        detailData.sql_type = stepValues.sql_type || 'select'
        detailData.sql = stepValues.sql || ''
        detailData.expected = stepValues.expected || ''
      } else if (stepTypeStr === 'script') {
        detailData.language = stepValues.language || 'javascript'
        detailData.content = stepValues.content || ''
        detailData.timeout = Number(stepValues.timeout) || 60
      }
      await scenarioApi.updateStepDetail(editingScenario.id, stepId, detailData)
    }

    // 更新步骤详情缓存（api 编辑模式），避免切换步骤后回显旧数据
    if (stepDetailMode === 'api_detail' || stepDetailMode === 'new_request') {
      const effectiveBodyType = stepValues.body_type || stepBodyType || 'none'
      const bodyFormEntries = stepValues.bodyFormEntries || []
      let finalBody = stepValues.body || ''
      if ((effectiveBodyType === 'form-data' || effectiveBodyType === 'x-www-form-urlencoded') && bodyFormEntries.length) {
        finalBody = JSON.stringify(
          bodyFormEntries
            .filter((e: any) => e.key && e.key.trim())
            .map((e: any) => ({ key: e.key, type: e.value || 'string', value: e.remark || '' })),
        )
      }
      const apiId = selectedImportApi?.id
      if (apiId) {
        extractRulesCacheRef.current.set(stepId, JSON.stringify(stepExtractRules))
        stepDetailCacheRef.current.set(stepId, {
          detail: { api_id: apiId, extract_rules: JSON.stringify(stepExtractRules) },
          api: {
            ...(selectedImportApi as ApiDefinition),
            name: stepValues.name,
            method: stepValues.method,
            path: stepValues.path,
            host: stepValues.host || '',
            headers: kvArrayToJson(stepValues.headerEntries || []),
            query_params: kvArrayToJson(stepValues.paramEntries || []),
            rest_params: kvArrayToJson(stepValues.restEntries || []),
            body_type: effectiveBodyType,
            body: finalBody,
            description: stepValues.description || '',
            assertions: JSON.stringify(stepAssertionRules),
          } as ApiDefinition,
        })
      } else {
        stepDetailCacheRef.current.delete(stepId)
      }
    }
  }

  const handleSave = async () => {
    try {
      const values = await scenarioForm.validateFields()
      setSubmitting(true)
      // 保存场景前先持久化所有未保存的临时步骤
      const tempSteps = steps.filter((s) => s.id < 0)
      if (editingScenario && tempSteps.length > 0) {
        // 合并当前选中临时步骤的最新表单数据到快照副本
        const mergedSnapshots = { ...tempStepSnapshotsRef.current }
        if (selectedStepId !== null && selectedStepId < 0) {
          const currentStep = steps.find((s) => s.id === selectedStepId)
          if (currentStep) {
            mergedSnapshots[selectedStepId] = {
              api: selectedImportApi,
              detailMode: stepDetailMode,
              formValues: stepForm.getFieldsValue(true),
              assertionRules: stepAssertionRules,
              extractRules: stepExtractRules,
              bodyType: stepBodyType,
            }
          }
        }
        for (const tempStep of tempSteps) {
          await persistTempStep(editingScenario.id, tempStep.id, tempStep.sort, mergedSnapshots, tempStep.parent_id ?? null)
        }
      }
      // 当前选中的已存在步骤若有未保存修改，随「保存场景」一并持久化（校验失败则中断保存）
      if (editingScenario && selectedStepId !== null && selectedStepId > 0) {
        await persistExistingStep(selectedStepId)
      }
      if (editingScenario) {
        await scenarioApi.updateScenario(editingScenario.id, {
          name: values.name,
          description: values.description || '',
          environment: values.environment,
          module_id: values.module_id || null,
        })
        message.success('更新成功')
      } else {
        await scenarioApi.createScenario({
          project_id: projectId,
          module_id: values.module_id || null,
          name: values.name,
          description: values.description || '',
          environment: values.environment || envList[0]?.url || '',
          creator_id: user?.id || 1,
        })
        message.success('创建成功')
      }
      // 保存完成后清空临时步骤快照
      tempStepSnapshotsRef.current = {}
      setDrawerOpen(false)
      loadScenarios()
    } catch {
      // 表单校验或接口错误
    } finally {
      setSubmitting(false)
    }
  }

  // 场景列表"执行"：不打开编辑抽屉，直接执行该场景（运行完成生成测试报告并跳转报告页）
  const navigate = useNavigate()
  const handleExecute = async (record: Scenario) => {
    if (executingIds[record.id]) return
    setExecutingIds((prev) => ({ ...prev, [record.id]: true }))
    try {
      let stepList: ScenarioStep[] = []
      try {
        stepList = await scenarioApi.listSteps(record.id)
      } catch {
        stepList = []
      }
      await handleRunScenario(record, stepList)
      navigate('/reports')
    } finally {
      setExecutingIds((prev) => ({ ...prev, [record.id]: false }))
      // 刷新列表（运行状态/报告计数等）
      loadScenarios()
    }
  }

  // ============ 步骤操作 ============

  const handleAddStepByType = async (action: StepCreateAction, parentStepId?: number) => {
    if (!editingScenario) return
    // 子步骤仅支持添加到已保存的父步骤（避免 parent_id 指向临时步骤）
    if (parentStepId && parentStepId < 0) {
      message.warning('请先保存父步骤，再添加其内部步骤')
      return
    }
    const backendType = actionToStepType[action]
    const label = actionToStepTypeLabel[action]
    const newSort = parentStepId
      ? steps.filter((s) => s.parent_id === parentStepId).length
      : steps.filter((s) => !s.parent_id).length

    if (action === 'import_api') {
      setSelectedImportApi(null)
      loadApiList(1)
      // 记录导入目标父步骤（子步骤下拉触发时），确认后创建为该父步骤的子步骤
      setImportParentStepId(parentStepId ?? null)
      setApiImportModalOpen(true)
      return
    }

    const tempId = -(Date.now())
    // 添加子步骤时自动展开父步骤的作用域区域
    if (parentStepId) {
      setExpandedStepIds((prev) => ({ ...prev, [parentStepId]: true }))
    }
    const tempStep: ScenarioStep = {
      id: tempId,
      scenario_id: editingScenario.id,
      name: label,
      step_type: stepTypeNumberToLabel[backendType],
      sort: newSort,
      parent_id: parentStepId ?? null,
      create_time: '',
      update_time: '',
    }
    setSteps((prev) => [...prev, tempStep])
    setSelectedStepIdSync(tempId)
    setStepActiveTab('headers')
    setStepBodyType('none')
    setStepResponseResult(null)
    setStepResponseTab('body')
    setStepAssertionRules([])
    setStepAssertionResults([])
    // 参数提取规则随新步骤重置，避免残留上一步骤的提取配置
    setStepExtractRules([])
    setStepExtractResults([])

    if (action === 'new_request') {
      setStepDetailMode('new_request')
      setSelectedImportApi(null)
      stepForm.setFieldsValue({
        name: label,
        step_type: backendType,
        method: 'GET',
        path: '',
        host: '',
        headerEntries: [],
        paramEntries: [],
        restEntries: [],
        body_type: 'none',
        body: '',
        bodyFormEntries: [],
        description: '',
      })
      applyStepHost()
      tempStepSnapshotsRef.current[tempId] = {
        api: null,
        detailMode: 'new_request',
        formValues: stepForm.getFieldsValue(true),
        assertionRules: [],
        extractRules: [],
        bodyType: 'none',
      }
    } else {
      setStepDetailMode('step_form')
      stepForm.setFieldsValue({
        name: label,
        step_type: backendType,
      })
      tempStepSnapshotsRef.current[tempId] = {
        api: null,
        detailMode: 'step_form',
        formValues: stepForm.getFieldsValue(true),
        assertionRules: [],
        extractRules: [],
        bodyType: 'none',
      }
    }
  }

  const handleDeleteStep = async (stepId: number) => {
    if (!editingScenario) return
    try {
      if (stepId >= 0) {
        await scenarioApi.deleteStep(editingScenario.id, stepId)
      }
      // 父步骤删除时级联移除其作用域内的子步骤（后端已级联软删除）
      setSteps((prev) => prev.filter((s) => s.id !== stepId && s.parent_id !== stepId))
      // 清理被删除临时步骤的快照
      delete tempStepSnapshotsRef.current[stepId]
      if (selectedStepId === stepId) {
        setSelectedStepIdSync(null)
        stepForm.resetFields()
      }
    } catch {
      // 错误已在拦截器处理
    }
  }

  const handleSelectStep = async (step: ScenarioStep) => {
    // 切换前保存当前临时步骤的表单数据到快照
    saveCurrentTempSnapshot()
    setSelectedStepIdSync(step.id)
    setStepActiveTab('headers')
    setStepBodyType('none')
    // 该步骤若有运行场景的响应结果则继续展示，否则清空
    setStepResponseResult(stepRunResults[step.id] || null)
    setStepResponseTab('body')
    setStepAssertionRules([])
    setStepAssertionResults([])

    const isTemp = step.id < 0

    stepForm.setFieldsValue({
      name: step.name,
      step_type: step.step_type,
    })

    if (step.step_type === 'http') {
      // HTTP 步骤：在异步加载详情前，立即设置 stepDetailMode，避免闪现 StepNonHttpEditor
      if (isTemp) {
        const snapshot = tempStepSnapshotsRef.current[step.id]
        if (snapshot) {
          // 立即设置模式，避免渲染 StepNonHttpEditor
          setStepDetailMode(snapshot.detailMode)
          setSelectedImportApi(snapshot.api)
          // 先重置表单清除旧步骤残留字段，再设置快照数据
          stepForm.resetFields()
          stepForm.setFieldsValue(snapshot.formValues)
          setStepBodyType(snapshot.bodyType)
          setStepAssertionRules(withUniqueRuleIds(snapshot.assertionRules, 'assert_', assertionIdRef))
          setStepExtractRules(withUniqueRuleIds(snapshot.extractRules || [], 'extract_', extractIdRef))
          setStepExtractResults([])
          applyStepHost()
        } else {
          // 无快照兜底：按新增请求处理
          setStepDetailMode('new_request')
          setSelectedImportApi(null)
          stepForm.setFieldsValue({
            name: step.name,
            method: 'GET',
            path: '',
            host: '',
            headerEntries: [],
            paramEntries: [],
            restEntries: [],
            body_type: 'none',
            body: '',
            bodyFormEntries: [],
            description: '',
          })
          applyStepHost()
          setStepBodyType('none')
          setStepAssertionRules([])
          setStepExtractRules([])
          setStepExtractResults([])
        }
      } else {
        // 持久化 HTTP 步骤：优先读缓存，命中则同步渲染（一步到位）
        const cached = stepDetailCacheRef.current.get(step.id)
        if (cached) {
          // 缓存命中：同步设置所有数据，无网络请求，一步到位
          if (cached.detail?.api_id && cached.api) {
            setStepDetailMode('api_detail')
            setSelectedImportApi(cached.api)
            stepForm.setFieldsValue({
              name: cached.api.name || step.name,
              method: cached.api.method || 'GET',
              path: cached.api.path || '',
              host: cached.api.host || '',
              headerEntries: jsonToKvArray(cached.api.headers || '[]'),
              paramEntries: jsonToKvArray(cached.api.query_params || '[]'),
              restEntries: jsonToKvArray(cached.api.rest_params || '[]'),
              body_type: cached.api.body_type || 'none',
              body: cached.api.body || '',
              bodyFormEntries: loadBodyFormEntries(cached.api.body || '[]', cached.api.body_type),
              description: cached.api.description || '',
            })
            applyStepHost()
            setStepBodyType(cached.api.body_type || 'none')
            try {
              const saved = JSON.parse(cached.api.assertions || '[]')
              setStepAssertionRules(withUniqueRuleIds(saved, 'assert_', assertionIdRef))
            } catch {
              setStepAssertionRules([])
            }
            try {
              const savedExtracts = JSON.parse(cached.detail?.extract_rules || '[]')
              setStepExtractRules(withUniqueRuleIds(savedExtracts, 'extract_', extractIdRef))
            } catch {
              setStepExtractRules([])
            }
            setStepExtractResults([])
          } else {
            setStepDetailMode('new_request')
            setSelectedImportApi(null)
            const d = cached.detail
            stepForm.setFieldsValue({
              name: d?.name || step.name,
              method: d?.method || 'GET',
              path: d?.path || '',
              host: d?.host || '',
              headerEntries: jsonToKvArray(d?.headers || '[]'),
              paramEntries: jsonToKvArray(d?.query_params || '[]'),
              restEntries: jsonToKvArray(d?.rest_params || '[]'),
              body_type: d?.body_type || 'none',
              body: d?.body || '',
              bodyFormEntries: loadBodyFormEntries(d?.body || '[]', d?.body_type || 'none'),
              description: d?.description || '',
            })
            applyStepHost()
            setStepBodyType(d?.body_type || 'none')
            setStepAssertionRules([])
            try {
              const savedExtracts = JSON.parse(d?.extract_rules || '[]')
              setStepExtractRules(withUniqueRuleIds(savedExtracts, 'extract_', extractIdRef))
            } catch {
              setStepExtractRules([])
            }
            setStepExtractResults([])
          }
        } else {
          // 缓存未命中：显示 loading 占位，加载完成后填充表单并写入缓存
          setStepDetailLoading(true)
          try {
            const detail = await scenarioApi.getStepDetail(editingScenario!.id, step.id)
            if (detail?.api_id) {
              const api = await apiDefApi.getApiDefinition(detail.api_id, projectId)
              // 写入缓存
              stepDetailCacheRef.current.set(step.id, { detail, api })
              extractRulesCacheRef.current.set(step.id, detail?.extract_rules || '[]')
              setStepDetailMode('api_detail')
              setSelectedImportApi(api)
              stepForm.setFieldsValue({
                name: api.name || step.name,
                method: api.method || 'GET',
                path: api.path || '',
                host: api.host || '',
                headerEntries: jsonToKvArray(api.headers || '[]'),
                paramEntries: jsonToKvArray(api.query_params || '[]'),
                restEntries: jsonToKvArray(api.rest_params || '[]'),
                body_type: api.body_type || 'none',
                body: api.body || '',
                bodyFormEntries: loadBodyFormEntries(api.body || '[]', api.body_type),
                description: api.description || '',
              })
              applyStepHost()
              setStepBodyType(api.body_type || 'none')
              try {
                const saved = JSON.parse(api.assertions || '[]')
                setStepAssertionRules(withUniqueRuleIds(saved, 'assert_', assertionIdRef))
              } catch {
                setStepAssertionRules([])
              }
              try {
                const savedExtracts = JSON.parse(detail?.extract_rules || '[]')
                setStepExtractRules(withUniqueRuleIds(savedExtracts, 'extract_', extractIdRef))
              } catch {
                setStepExtractRules([])
              }
              setStepExtractResults([])
            } else {
              // 步骤详情无 api_id，按 new_request 处理
              stepDetailCacheRef.current.set(step.id, { detail, api: null })
              extractRulesCacheRef.current.set(step.id, detail?.extract_rules || '[]')
              setStepDetailMode('new_request')
              setSelectedImportApi(null)
              stepForm.setFieldsValue({
                name: detail?.name || step.name,
                method: detail?.method || 'GET',
                path: detail?.path || '',
                host: detail?.host || '',
                headerEntries: jsonToKvArray(detail?.headers || '[]'),
                paramEntries: jsonToKvArray(detail?.query_params || '[]'),
                restEntries: jsonToKvArray(detail?.rest_params || '[]'),
                body_type: detail?.body_type || 'none',
                body: detail?.body || '',
                bodyFormEntries: loadBodyFormEntries(detail?.body || '[]', detail?.body_type || 'none'),
                description: detail?.description || '',
              })
              applyStepHost()
              setStepBodyType(detail?.body_type || 'none')
              try {
                const saved = JSON.parse(detail?.assertions || '[]')
                setStepAssertionRules(withUniqueRuleIds(saved, 'assert_', assertionIdRef))
              } catch {
                setStepAssertionRules([])
              }
              try {
                const savedExtracts = JSON.parse(detail?.extract_rules || '[]')
                setStepExtractRules(withUniqueRuleIds(savedExtracts, 'extract_', extractIdRef))
              } catch {
                setStepExtractRules([])
              }
              setStepExtractResults([])
            }
          } catch {
            setStepDetailMode('new_request')
            setSelectedImportApi(null)
            stepForm.resetFields()
          } finally {
            setStepDetailLoading(false)
          }
        }
      }
    } else {
      setStepDetailMode('step_form')
      setSelectedImportApi(null)
      if (isTemp) {
        stepForm.setFieldsValue({
          name: step.name,
          step_type: step.step_type,
        })
        if (step.step_type === 'wait') {
          stepForm.setFieldsValue({
            duration_ms: 0,
            condition: '',
          })
        } else if (step.step_type === 'condition') {
          stepForm.setFieldsValue({
            expression: '',
            expected_value: '',
          })
        } else if (step.step_type === 'loop') {
          stepForm.setFieldsValue({
            loop_type: 'count',
            times: 1,
            max_retries: 0,
          })
        } else if (step.step_type === 'assertion') {
          setStepAssertionRules([])
        } else if (step.step_type === 'db') {
          stepForm.setFieldsValue({
            datasource_id: undefined,
            sql_type: 'select',
            sql: '',
            expected: '',
          })
        } else if (step.step_type === 'script') {
          stepForm.setFieldsValue({
            language: 'javascript',
            content: '',
            timeout: 60,
          })
        }
      } else {
        // 加载非 HTTP 步骤详情
        try {
          const detail = await scenarioApi.getStepDetail(editingScenario!.id, step.id)
          if (detail) {
            if (step.step_type === 'wait') {
              stepForm.setFieldsValue({
                name: detail.name || step.name,
                duration_ms: detail.duration_ms || 0,
                condition: detail.condition || '',
              })
            } else if (step.step_type === 'condition') {
              stepForm.setFieldsValue({
                name: detail.name || step.name,
                expression: detail.expression || '',
                expected_value: detail.expected_value || '',
              })
            } else if (step.step_type === 'loop') {
              stepForm.setFieldsValue({
                name: detail.name || step.name,
                loop_type: detail.loop_type || 'count',
                times: detail.times || 1,
                max_retries: detail.max_retries || 0,
              })
            } else if (step.step_type === 'assertion') {
              stepForm.setFieldsValue({
                name: detail.name || step.name,
              })
              try {
                const saved = JSON.parse(detail.rules || '[]')
                if (Array.isArray(saved)) {
                  setStepAssertionRules(withUniqueRuleIds(saved, 'assert_', assertionIdRef))
                } else {
                  setStepAssertionRules([])
                }
              } catch {
                setStepAssertionRules([])
              }
            } else if (step.step_type === 'db') {
              stepForm.setFieldsValue({
                name: detail.name || step.name,
                datasource_id: detail.datasource_id,
                sql_type: detail.sql_type || 'select',
                sql: detail.sql || '',
                expected: detail.expected || '',
              })
            } else if (step.step_type === 'script') {
              stepForm.setFieldsValue({
                name: detail.name || step.name,
                language: detail.language || 'javascript',
                content: detail.content || '',
                timeout: detail.timeout || 60,
              })
            }
          }
        } catch {
          // ignore
        }
      }
    }

    // 若该步骤有运行场景的断言/提取结果，则恢复展示（覆盖分支中的清空），否则清空
    setStepAssertionResults(stepRunAssertionResults[step.id] || [])
    setStepExtractResults(stepRunExtractResults[step.id] || [])
  }

  // 防止并发保存导致重复创建步骤
  const savingStepRef = useRef(false)
  const handleSaveStep = async () => {
    if (!editingScenario || !selectedStepId) return
    // 防止并发保存（连点）：使用 ref 同步读取最新步骤ID
    if (savingStepRef.current) return
    savingStepRef.current = true
    try {
      const stepValues = await readStepFormValues()

      const stepTypeNumber =
        typeof stepValues.step_type === 'string'
          ? { http: 1, wait: 2, condition: 3, loop: 4, assertion: 5, db: 6, script: 7 }[stepValues.step_type as string] || 1
          : stepValues.step_type
      // 归一化为字符串（新增步骤时表单写入的是数字），用于详情字段分支判断
      const stepTypeStr =
        typeof stepValues.step_type === 'string'
          ? stepValues.step_type
          : ({ 1: 'http', 2: 'wait', 3: 'condition', 4: 'loop', 5: 'assertion', 6: 'db', 7: 'script' } as Record<number, string>)[stepValues.step_type as number] || 'http'

      // 使用 ref 读取最新值，防止异步状态未更新导致重复创建
      const currentStepId = selectedStepIdRef.current ?? selectedStepId
      const isTemp = currentStepId < 0
      let realStepId = currentStepId

      if (isTemp) {
        const tempStepInfo = steps.find((s) => s.id === currentStepId)
        const isChildStep = !!tempStepInfo?.parent_id
        const createdStep = await scenarioApi.createStep(editingScenario.id, {
          name: stepValues.name,
          step_type: stepTypeNumber,
          sort: tempStepInfo?.sort ?? 0,
          parent_id: tempStepInfo?.parent_id ?? null,
        })
        realStepId = createdStep?.id
        if (!realStepId) {
          message.error('步骤创建失败，未获取到步骤ID')
          return
        }
        // 立即更新步骤ID和列表，防止后续详情保存失败导致重复创建步骤
        setSelectedStepIdSync(realStepId)
        setSteps((prev) =>
          prev.map((s) => (s.id === currentStepId ? { ...createdStep, step_type: stepTypeStr as any } : s)),
        )
        delete tempStepSnapshotsRef.current[currentStepId]

        // 子步骤 sort 在创建时已按父作用域指定，无需重排；顶层步骤按前端顺序持久化排序，
        // 避免拖拽插入时 reorder 仅含持久化步骤导致新步骤 sort 冲突被排到末尾
        if (!isChildStep) {
          const orderedIds = steps
            .filter((s) => !s.parent_id)
            .map((s) => (s.id === currentStepId ? realStepId : s.id))
            .filter((id) => id > 0)
          if (orderedIds.length > 0) {
            await scenarioApi.reorderSteps(editingScenario.id, orderedIds)
          }
        }

        if (stepDetailMode === 'api_detail' || stepDetailMode === 'new_request') {
          const apiValues = await readStepFormValues()
          const effectiveBodyType = apiValues.body_type || stepBodyType || 'none'
          const bodyFormEntries = apiValues.bodyFormEntries || []
          const bodyValue = apiValues.body || ''
          let finalBody = bodyValue
          if ((effectiveBodyType === 'form-data' || effectiveBodyType === 'x-www-form-urlencoded') && bodyFormEntries.length) {
            finalBody = JSON.stringify(
              bodyFormEntries
                .filter((e: any) => e.key && e.key.trim())
                .map((e: any) => ({ key: e.key, type: e.value || 'string', value: e.remark || '' })),
            )
          }

          const apiPayload = {
            project_id: projectId,
            name: apiValues.name,
            method: apiValues.method,
            path: apiValues.path,
            host: apiValues.host || '',
            headers: kvArrayToJson(apiValues.headerEntries || []),
            query_params: kvArrayToJson(apiValues.paramEntries || []),
            rest_params: kvArrayToJson(apiValues.restEntries || []),
            body_type: effectiveBodyType,
            body: finalBody,
            description: apiValues.description || '',
            assertions: JSON.stringify(stepAssertionRules),
          }

          let apiId = selectedImportApi?.id
          if (stepDetailMode === 'api_detail' && apiId) {
            await apiDefApi.updateApiDefinition(apiId, apiPayload, projectId)
          } else if (stepDetailMode === 'new_request') {
            if (apiId) {
              await apiDefApi.updateApiDefinition(apiId, apiPayload, projectId)
            } else {
              const created = await apiDefApi.createApiDefinition({
                ...apiPayload,
                creator_id: user?.id || 1,
              })
              apiId = created.id
            }
          }

          extractRulesCacheRef.current.set(realStepId, JSON.stringify(stepExtractRules))
          await scenarioApi.createStepDetail(editingScenario.id, realStepId, {
            api_id: apiId,
            pre_process: '',
            post_process: '',
            extract_rules: JSON.stringify(stepExtractRules),
          })
        } else if (stepDetailMode === 'step_form') {
          // name 存于步骤表（由 createStep/updateStep 保存），详情表无 name 列
          const detailData: Record<string, any> = {}
          if (stepTypeStr === 'wait') {
            detailData.duration_ms = Number(stepValues.duration_ms) || 0
            detailData.condition = stepValues.condition || ''
          } else if (stepTypeStr === 'condition') {
            detailData.expression = stepValues.expression || ''
            detailData.expected_value = stepValues.expected_value || ''
          } else if (stepTypeStr === 'loop') {
            detailData.loop_type = stepValues.loop_type || 'count'
            detailData.times = Number(stepValues.times) || 1
            detailData.max_retries = Number(stepValues.max_retries) || 0
          } else if (stepTypeStr === 'assertion') {
            detailData.rules = JSON.stringify(stepAssertionRules)
          } else if (stepTypeStr === 'db') {
            detailData.datasource_id = stepValues.datasource_id
            detailData.sql_type = stepValues.sql_type || 'select'
            detailData.sql = stepValues.sql || ''
            detailData.expected = stepValues.expected || ''
          } else if (stepTypeStr === 'script') {
            detailData.language = stepValues.language || 'javascript'
            detailData.content = stepValues.content || ''
            detailData.timeout = Number(stepValues.timeout) || 60
          }
          await scenarioApi.createStepDetail(editingScenario.id, realStepId, detailData)
        }
      } else {
        // 已存在步骤：统一走 persistExistingStep（「保存场景」也复用该逻辑）
        await persistExistingStep(currentStepId)
      }

      const stepList = await scenarioApi.listSteps(editingScenario.id)
      setSteps(stepList || [])
      message.success(isTemp ? '步骤已保存' : '步骤已更新')
    } catch {
      // 表单校验或接口错误
    } finally {
      savingStepRef.current = false
    }
  }

  const renderStepApiEditor = () => {
    const currentMethod = stepForm.getFieldValue('method') || 'GET'
    return (
      <Form
        form={stepForm}
        layout="vertical"
        style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
        onFocusCapture={(e) => {
          // 记录步骤编辑区内最近聚焦的输入框（供「引用变量」插入光标处）；下拉选择器输入框除外
          const t = e.target as HTMLElement
          if ((t.tagName === 'INPUT' || t.tagName === 'TEXTAREA') && !t.closest('.ant-select')) {
            lastFocusedEditableRef.current = t as HTMLInputElement | HTMLTextAreaElement
          }
        }}
        onValuesChange={(changedValues) => {
          // 同步步骤名称到左侧步骤列表
          if ('name' in changedValues && selectedStepId !== null) {
            setSteps((prev) => prev.map((s) => (s.id === selectedStepId ? { ...s, name: changedValues.name } : s)))
          }
        }}
      >
        <Form.Item name="body_type" hidden>
          <Input />
        </Form.Item>
        {/* 顶部：方法标签 + 标题（紧凑高度） */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          padding: '6px 16px',
          borderBottom: isDark ? '1px solid var(--border)' : '1px solid var(--au-border)',
          background: isDark ? 'var(--card)' : '#ffffff',
          flexShrink: 0,
          gap: 12,
        }}>
          <Tag
            color={methodColor[currentMethod as keyof typeof methodColor] || 'default'}
            style={{
              fontSize: 14,
              fontWeight: 600,
              padding: '2px 10px',
              margin: 0,
            }}
          >
            {currentMethod}
          </Tag>
          <Form.Item name="name" style={{ margin: 0, flex: 1 }}>
            <Input
              bordered={false}
              style={{ fontSize: 16, fontWeight: 600 }}
            />
          </Form.Item>
        </div>

        {/* 请求行：方法 + 环境 + URL + 调试 + 保存 */}
        <div className="aurora-urlbar" style={{
          display: 'flex',
          alignItems: 'center',
          padding: '12px 16px',
          gap: 0,
          borderBottom: isDark ? '1px solid var(--border)' : '1px solid var(--au-border)',
          background: isDark ? 'var(--card)' : '#ffffff',
          flexShrink: 0,
        }}>
          <div style={{
            width: 90,
            border: '1px solid var(--au-border)',
            borderRadius: '4px 0 0 4px',
          }}>
            <Form.Item name="method" style={{ margin: 0 }}>
              <Select
                options={methodOptions.map(m => ({ ...m, color: methodColor[m.value as keyof typeof methodColor] }))}
                optionRender={(opt) => (
                  <span style={{ color: (opt.data as any).color, fontWeight: 600 }}>
                    {opt.label}
                  </span>
                )}
                labelRender={(props) => (
                  <span style={{ color: methodColor[props.value as keyof typeof methodColor] || '#06b6d4', fontWeight: 600 }}>
                    {props.label}
                  </span>
                )}
                bordered={false}
                style={{ width: '100%' }}
              />
            </Form.Item>
          </div>
          <div style={{
            width: 160,
            border: '1px solid var(--au-border)',
            borderRight: 'none',
          }}>
            <Form.Item name="host" style={{ margin: 0 }}>
              <Select
                placeholder="环境"
                allowClear
                bordered={false}
                style={{ width: '100%' }}
                options={envList.map((env) => ({ value: env.url, label: env.url }))}
              />
            </Form.Item>
          </div>
          <Form.Item name="path" style={{ margin: 0, flex: 1 }}>
            <Input
              placeholder="请输入URL，可通过右侧「引用变量」插入上面步骤提取的参数"
              style={{ borderRadius: '0 6px 6px 0', borderLeft: 'none' }}
            />
          </Form.Item>
          <Dropdown
            trigger={['click']}
            placement="bottomRight"
            menu={{
              items: buildExtractVarMenuItems(),
              onClick: ({ key }) => {
                if (key !== '__empty__') insertExtractVariable(key)
              },
            }}
          >
            <Button icon={<ShareAltOutlined />} style={{ marginLeft: 8 }}>
              引用变量
            </Button>
          </Dropdown>
          <Button
            type="primary"
            loading={stepDebugging}
            onClick={handleStepDebug}
            className="pill-btn"
            style={{
              marginLeft: 10,
              background: 'var(--au-primary)',
              border: 'none',
            }}
          >
            调试
          </Button>
        </div>

        {/* Tabs 区域 */}
        <div style={{
          flex: '1 1 200px',
          minHeight: 200,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}>
          <Tabs
            activeKey={stepActiveTab}
            onChange={setStepActiveTab}
            tabBarStyle={{ paddingLeft: 16 }}
            style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}
            items={[
              {
                key: 'headers',
                label: '请求头',
                children: (
                  <div className="thin-scrollbar" style={{ padding: '0 16px 150px', maxHeight: 'min(340px, 38vh)', overflowY: 'auto' }}>
                    {renderKvTable('headerEntries', { key: 'Header Key', value: 'Header Value', remark: '描述' }, stepCommonHeaders, isDark)}
                  </div>
                ),
              },
              {
                key: 'body',
                label: '请求体',
                children: (
                  <div style={{ padding: '0 16px 16px' }}>
                    <div style={{ marginBottom: 12 }}>
                      {bodyTypeOptions.map((opt) => (
                        <Text
                          key={opt.value}
                          onClick={() => { stepForm.setFieldsValue({ body_type: opt.value }); setStepBodyType(opt.value) }}
                          style={{
                            cursor: 'pointer',
                            padding: '4px 12px',
                            marginRight: 8,
                            borderRadius: 4,
                            fontSize: 13,
                            color: stepBodyType === opt.value ? 'var(--au-primary)' : (isDark ? '#94A4B8' : 'var(--au-slate-muted)'),
                            background: stepBodyType === opt.value ? (isDark ? 'rgba(255,255,255,0.12)' : 'var(--au-primary-50)') : (isDark ? 'rgba(255,255,255,0.06)' : 'var(--au-row-alt)'),
                            border: stepBodyType === opt.value ? '1px solid var(--au-primary)' : (isDark ? '1px solid var(--border)' : '1px solid transparent'),
                          }}
                        >
                          {opt.label}
                        </Text>
                      ))}
                    </div>
                    {stepBodyType === 'none' && (
                      <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--au-slate-muted)', fontSize: 14 }}>
                        该请求没有 Body
                      </div>
                    )}
                    {stepBodyType === 'form-data' && (
                      <Form.List name="bodyFormEntries">
                        {(fields, { add, remove }) => (
                          <div style={{ border: isDark ? '1px solid var(--border)' : '1px solid var(--au-border)', borderRadius: 4, overflow: 'hidden' }}>
                            <div style={{
                              display: 'grid',
                              gridTemplateColumns: '40px 1fr 100px 1fr 60px',
                              gap: 0,
                              padding: '8px 12px',
                              background: isDark ? 'var(--bg)' : 'var(--au-header-bg)',
                              borderBottom: isDark ? '1px solid var(--border)' : '1px solid var(--au-border)',
                              color: isDark ? 'var(--text-light)' : 'var(--au-slate)',
                              fontWeight: 500,
                              fontSize: 13,
                              alignItems: 'center',
                            }}>
                              <Checkbox disabled defaultChecked />
                              <span>参数名称</span>
                              <span>类型</span>
                              <span>参数值</span>
                              <Button type="link" size="small" onClick={() => add({ key: '', value: 'string', remark: '' })} style={{ padding: 0 }}>
                                添加
                              </Button>
                            </div>
                            {fields.length === 0 && (
                              <div style={{ padding: '24px 0', color: isDark ? 'var(--text-lighter)' : 'var(--au-slate-muted)', fontSize: 13, textAlign: 'center' }}>
                                暂无数据，点击右上角"添加"新增
                              </div>
                            )}
                            {fields.map(({ key, name: fieldName, ...restField }, index) => (
                              <div
                                key={key}
                                style={{
                                  display: 'grid',
                                  gridTemplateColumns: '40px 1fr 100px 1fr 60px',
                                  gap: 0,
                                  padding: '4px 12px',
                                  alignItems: 'center',
                                  borderBottom: isDark ? '1px solid var(--border)' : '1px solid var(--au-row-alt)',
                                  background: isDark
                                    ? (index % 2 === 0 ? 'var(--bg)' : 'var(--card)')
                                    : (index % 2 === 0 ? '#ffffff' : 'var(--au-header-bg)'),
                                }}
                              >
                                <Checkbox defaultChecked />
                                <Form.Item {...restField} name={[fieldName, 'key']} style={{ marginBottom: 0, marginRight: 8 }}>
                                  <Input size="small" placeholder="参数名称" bordered={false} />
                                </Form.Item>
                                <Form.Item {...restField} name={[fieldName, 'value']} style={{ marginBottom: 0, marginRight: 8 }}>
                                  <Select
                                    size="small"
                                    bordered={false}
                                    options={stepFormDataTypeOptions}
                                    style={{ width: '100%' }}
                                  />
                                </Form.Item>
                                <Form.Item {...restField} name={[fieldName, 'remark']} style={{ marginBottom: 0, marginRight: 8 }}>
                                  <Input size="small" placeholder="参数值" bordered={false} />
                                </Form.Item>
                                <Button type="text" size="small" danger onClick={() => remove(fieldName)} style={{ padding: 0 }}>
                                  删除
                                </Button>
                              </div>
                            ))}
                          </div>
                        )}
                      </Form.List>
                    )}
                    {stepBodyType === 'x-www-form-urlencoded' && (
                      <Form.List name="bodyFormEntries">
                        {(fields, { add, remove }) => (
                          <div style={{ border: isDark ? '1px solid var(--border)' : '1px solid var(--au-border)', borderRadius: 4, overflow: 'hidden' }}>
                            <div style={{
                              display: 'grid',
                              gridTemplateColumns: '40px 1fr 100px 1fr 60px',
                              gap: 0,
                              padding: '8px 12px',
                              background: isDark ? 'var(--bg)' : 'var(--au-header-bg)',
                              borderBottom: isDark ? '1px solid var(--border)' : '1px solid var(--au-border)',
                              color: isDark ? 'var(--text-light)' : 'var(--au-slate)',
                              fontWeight: 500,
                              fontSize: 13,
                              alignItems: 'center',
                            }}>
                              <Checkbox disabled defaultChecked />
                              <span>参数名称</span>
                              <span>类型</span>
                              <span>参数值</span>
                              <Button type="link" size="small" onClick={() => add({ key: '', value: 'string', remark: '' })} style={{ padding: 0 }}>
                                添加
                              </Button>
                            </div>
                            {fields.length === 0 && (
                              <div style={{ padding: '24px 0', color: isDark ? 'var(--text-lighter)' : 'var(--au-slate-muted)', fontSize: 13, textAlign: 'center' }}>
                                暂无数据，点击右上角"添加"新增
                              </div>
                            )}
                            {fields.map(({ key, name: fieldName, ...restField }, index) => (
                              <div
                                key={key}
                                style={{
                                  display: 'grid',
                                  gridTemplateColumns: '40px 1fr 100px 1fr 60px',
                                  gap: 0,
                                  padding: '4px 12px',
                                  alignItems: 'center',
                                  borderBottom: isDark ? '1px solid var(--border)' : '1px solid var(--au-row-alt)',
                                  background: isDark
                                    ? (index % 2 === 0 ? 'var(--bg)' : 'var(--card)')
                                    : (index % 2 === 0 ? '#ffffff' : 'var(--au-header-bg)'),
                                }}
                              >
                                <Checkbox defaultChecked />
                                <Form.Item {...restField} name={[fieldName, 'key']} style={{ marginBottom: 0, marginRight: 8 }}>
                                  <Input size="small" placeholder="参数名称" bordered={false} />
                                </Form.Item>
                                <Form.Item {...restField} name={[fieldName, 'value']} style={{ marginBottom: 0, marginRight: 8 }}>
                                  <Select
                                    size="small"
                                    bordered={false}
                                    options={stepUrlencodedTypeOptions}
                                    style={{ width: '100%' }}
                                  />
                                </Form.Item>
                                <Form.Item {...restField} name={[fieldName, 'remark']} style={{ marginBottom: 0, marginRight: 8 }}>
                                  <Input size="small" placeholder="参数值" bordered={false} />
                                </Form.Item>
                                <Button type="text" size="small" danger onClick={() => remove(fieldName)} style={{ padding: 0 }}>
                                  删除
                                </Button>
                              </div>
                            ))}
                          </div>
                        )}
                      </Form.List>
                    )}
                    {(stepBodyType === 'json' || stepBodyType === 'xml' || stepBodyType === 'raw') && (
                      <div style={{ position: 'relative' }}>
                        <Button
                          size="small"
                          style={{ position: 'absolute', right: 0, top: -36, zIndex: 1 }}
                          onClick={() => {
                            const currentBody = stepForm.getFieldValue('body') || ''
                            if (!currentBody.trim()) {
                              message.warning('没有可格式化的内容')
                              return
                            }
                            if (stepBodyType === 'xml') {
                              let formatted = currentBody
                              try {
                                formatted = currentBody.replace(/></g, '>\n<')
                                const lines = formatted.split('\n')
                                let indent = 0
                                formatted = lines.map((line: string) => {
                                  const trimmed = line.trim()
                                  if (!trimmed) return ''
                                  if (trimmed.startsWith('</')) indent = Math.max(0, indent - 1)
                                  const result = '  '.repeat(indent) + trimmed
                                  if (!trimmed.startsWith('</') && !trimmed.endsWith('/>') && !trimmed.startsWith('<?xml')) indent++
                                  return result
                                }).filter((l: string) => l !== '').join('\n')
                              } catch (e) { console.error('[Scenarios] 格式化失败:', e) }
                              stepForm.setFieldsValue({ body: formatted })
                              message.success('已格式化')
                            } else {
                              try {
                                const parsed = JSON.parse(currentBody)
                                stepForm.setFieldsValue({ body: JSON.stringify(parsed, null, 2) })
                                message.success('已格式化')
                              } catch {
                                message.warning('不是有效的 JSON 格式')
                              }
                            }
                          }}
                        >
                          格式化
                        </Button>
                        <Form.Item name="body" style={{ marginBottom: 0 }}>
                          <Input.TextArea rows={10} placeholder="请输入请求体内容" />
                        </Form.Item>
                      </div>
                    )}
                    {stepBodyType === 'binary' && (
                      <Form.Item name="body" style={{ marginBottom: 0 }}>
                        <Upload.Dragger
                          maxCount={1}
                          beforeUpload={() => false}
                        >
                          <p className="ant-upload-drag-icon">
                            <UploadOutlined style={{ fontSize: 32, color: 'var(--au-primary)' }} />
                          </p>
                          <p style={{ color: 'var(--au-slate-muted)' }}>点击或拖拽文件到此区域上传</p>
                        </Upload.Dragger>
                      </Form.Item>
                    )}
                  </div>
                ),
              },
              {
                key: 'query',
                label: (
                  <Tooltip title="地址栏中跟在 ? 后面的参数，如 updateapi?id=1">
                    <span>Query <QuestionCircleOutlined style={{ fontSize: 12 }} /></span>
                  </Tooltip>
                ),
                children: (
                  <div style={{ padding: '0 16px 16px' }}>
                    {renderKvTable('paramEntries', { key: '参数名', value: '参数值', remark: '描述' }, undefined, isDark, stepParamTypeOptions)}
                  </div>
                ),
              },
              {
                key: 'rest',
                label: (
                  <Tooltip title="地址栏中被斜杠/分隔的参数，如 updateapi/{id}">
                    <span>REST <QuestionCircleOutlined style={{ fontSize: 12 }} /></span>
                  </Tooltip>
                ),
                children: (
                  <div style={{ padding: '0 16px 16px' }}>
                    {renderKvTable('restEntries', { key: '参数名', value: '默认值', remark: '描述' }, undefined, isDark, stepUrlencodedTypeOptions)}
                  </div>
                ),
              },
              { key: 'pre', label: '前置', children: <div style={{ padding: '24px', color: 'var(--au-slate-muted)' }}>前置脚本（待开发）</div> },
              { key: 'post', label: '后置', children: <div style={{ padding: '24px', color: 'var(--au-slate-muted)' }}>后置脚本（待开发）</div> },
              {
                key: 'assert',
                label: '断言规则',
                children: (
                  <div style={{ padding: '0 16px 16px' }}>
                    <Dropdown
                      menu={{
                        items: stepAssertionTypeOptions.map((opt) => ({
                          key: opt.value,
                          label: opt.label,
                          onClick: () => addStepAssertionRule(opt.value),
                        })),
                      }}
                    >
                      <Button type="primary" style={{ marginBottom: 12 }}>
                        添加断言规则
                      </Button>
                    </Dropdown>
                    {stepAssertionRules.length === 0 && (
                      <div style={{ padding: '24px 0', color: 'var(--au-slate-muted)', fontSize: 13, textAlign: 'center' }}>
                        暂无断言规则，点击上方按钮添加
                      </div>
                    )}
                    {stepAssertionRules.map((rule) => (
                      <div
                        key={rule.id}
                        style={{
                          border: isDark ? '1px solid var(--border)' : '1px solid var(--au-border)',
                          borderRadius: 4,
                          padding: '6px 10px',
                          marginBottom: 6,
                          background: isDark ? 'var(--bg)' : 'var(--au-header-bg)',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <Tag color="blue" style={{ margin: 0, flexShrink: 0 }}>{stepAssertionTypeOptions.find((o) => o.value === rule.type)?.label}</Tag>
                          {rule.type === 'response_header' && (
                            <AutoComplete
                              size="small"
                              placeholder="Header Key"
                              value={rule.key}
                              onChange={(v) => updateStepAssertionRule(rule.id, { key: v })}
                              style={{ width: 140 }}
                              options={stepCommonHeaders}
                              filterOption={(inputValue, option) =>
                                option!.value.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
                              }
                            />
                          )}
                          {rule.type === 'response_body' && (
                            <Input
                              size="small"
                              placeholder="表达式(选填)"
                              value={rule.expression}
                              onChange={(e) => updateStepAssertionRule(rule.id, { expression: e.target.value })}
                              style={{ width: 130 }}
                            />
                          )}
                          <Select
                            size="small"
                            value={rule.operator}
                            onChange={(v) => updateStepAssertionRule(rule.id, { operator: v })}
                            style={{ width: 100 }}
                            options={stepAssertionOperatorMap[rule.type] || []}
                          />
                          {!['is_empty', 'is_not_empty', 'is_null', 'is_not_null', 'field_exists', 'field_not_exists'].includes(rule.operator) && (
                            <Input
                              size="small"
                              placeholder={
                                rule.type === 'response_time' ? '毫秒数' :
                                rule.operator === 'regex_match' ? '正则表达式' :
                                rule.operator === 'type_is' ? '类型名' :
                                rule.operator?.startsWith('length') ? '长度值' :
                                rule.operator?.startsWith('greater') || rule.operator?.startsWith('less') ? '数值' :
                                '预期值'
                              }
                              value={rule.expected}
                              onChange={(e) => updateStepAssertionRule(rule.id, { expected: e.target.value })}
                              style={{ flex: 1 }}
                            />
                          )}
                          <Button
                            type="text"
                            size="small"
                            danger
                            onClick={() => removeStepAssertionRule(rule.id)}
                            style={{ padding: '0 4px', flexShrink: 0 }}
                          >
                            删除
                          </Button>
                        </div>
                        {stepAssertionResults.find((r) => r.id === rule.id) && (
                          <div style={{ marginTop: 4 }}>
                            {(() => {
                              const result = stepAssertionResults.find((r) => r.id === rule.id)!
                              return (
                                <Tag color={result.passed ? 'success' : 'error'} style={{ fontSize: 12 }}>
                                  {result.passed ? '通过' : '失败'}: {result.message}
                                </Tag>
                              )
                            })()}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ),
              },
              { key: 'settings', label: '设置', children: <div style={{ padding: '24px', color: 'var(--au-slate-muted)' }}>高级设置（待开发）</div> },
              {
                key: 'extract',
                label: '参数提取',
                children: (
                  <div style={{ padding: '0 16px 16px' }}>
                    <Button type="primary" style={{ marginBottom: 12 }} onClick={addStepExtractRule}>
                      添加提取规则
                    </Button>
                    {stepExtractRules.length === 0 && (
                      <div style={{ padding: '24px 0', color: 'var(--au-slate-muted)', fontSize: 13, textAlign: 'center' }}>
                        暂无提取规则，点击上方按钮添加
                      </div>
                    )}
                    {stepExtractRules.map((rule) => (
                      <div
                        key={rule.id}
                        style={{
                          border: isDark ? '1px solid var(--border)' : '1px solid var(--au-border)',
                          borderRadius: 4,
                          padding: '6px 10px',
                          marginBottom: 6,
                          background: isDark ? 'var(--bg)' : 'var(--au-header-bg)',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                          <Input
                            size="small"
                            placeholder="参数名称"
                            value={rule.name}
                            onChange={(e) => updateStepExtractRule(rule.id, { name: e.target.value })}
                            style={{ width: 140 }}
                          />
                          <Select
                            size="small"
                            value={rule.method}
                            onChange={(v) => updateStepExtractRule(rule.id, { method: v })}
                            style={{ width: 110 }}
                            options={stepExtractMethodOptions}
                          />
                          <Input
                            size="small"
                            placeholder="表达式，如 data.token 或正则 token=(\\w+)"
                            value={rule.expression}
                            onChange={(e) => updateStepExtractRule(rule.id, { expression: e.target.value })}
                            style={{ flex: 1, minWidth: 200 }}
                          />
                          <Input
                            size="small"
                            placeholder="描述（选填）"
                            value={rule.description}
                            onChange={(e) => updateStepExtractRule(rule.id, { description: e.target.value })}
                            style={{ width: 140 }}
                          />
                          <Button
                            type="text"
                            size="small"
                            danger
                            onClick={() => removeStepExtractRule(rule.id)}
                            style={{ padding: '0 4px', flexShrink: 0 }}
                          >
                            删除
                          </Button>
                        </div>
                        {stepExtractResults.find((r) => r.id === rule.id) && (
                          <div style={{ marginTop: 4 }}>
                            {(() => {
                              const result = stepExtractResults.find((r) => r.id === rule.id)!
                              return (
                                <Tag color={result.success ? 'success' : 'error'} style={{ fontSize: 12 }}>
                                  {result.success ? '成功' : '失败'}: {result.message}
                                </Tag>
                              )
                            })()}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ),
              },
            ]}
          />
        </div>

        {/* 响应内容 */}
        <div style={{
          borderTop: isDark ? '1px solid var(--border)' : '1px solid var(--au-border)',
          flex: '1 1 250px',
          minHeight: 200,
          display: 'flex',
          flexDirection: 'column',
          background: isDark ? 'var(--bg)' : '#ffffff',
          overflow: 'hidden',
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            padding: '6px 16px',
            borderBottom: isDark ? '1px solid var(--border)' : '1px solid var(--au-row-alt)',
            gap: 16,
            flexShrink: 0,
          }}>
            <Text style={{ fontWeight: 700, fontSize: 14, color: isDark ? 'var(--text)' : 'var(--au-slate)' }}>响应内容</Text>
            <Space size={16}>
              <Text
                style={{
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0 2px',
                  fontSize: 13,
                  fontWeight: 600,
                  color: stepResponseTab === 'body' ? 'var(--au-primary)' : 'var(--au-slate-muted)',
                  borderBottom: stepResponseTab === 'body' ? '2px solid var(--au-primary)' : '2px solid transparent',
                  transition: 'color 0.2s ease',
                }}
                onClick={() => setStepResponseTab('body')}
              >
                Body
              </Text>
              <Text
                style={{
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0 2px',
                  fontSize: 13,
                  fontWeight: 600,
                  color: stepResponseTab === 'headers' ? 'var(--au-primary)' : 'var(--au-slate-muted)',
                  borderBottom: stepResponseTab === 'headers' ? '2px solid var(--au-primary)' : '2px solid transparent',
                  transition: 'color 0.2s ease',
                }}
                onClick={() => setStepResponseTab('headers')}
              >
                Headers
              </Text>
              <Text
                style={{
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0 2px',
                  fontSize: 13,
                  fontWeight: 600,
                  color: stepResponseTab === 'assert' ? 'var(--au-primary)' : 'var(--au-slate-muted)',
                  borderBottom: stepResponseTab === 'assert' ? '2px solid var(--au-primary)' : '2px solid transparent',
                  transition: 'color 0.2s ease',
                }}
                onClick={() => setStepResponseTab('assert')}
              >
                断言结果
              </Text>
              <Text
                style={{
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0 2px',
                  fontSize: 13,
                  fontWeight: 600,
                  color: stepResponseTab === 'extract' ? 'var(--au-primary)' : 'var(--au-slate-muted)',
                  borderBottom: stepResponseTab === 'extract' ? '2px solid var(--au-primary)' : '2px solid transparent',
                  transition: 'color 0.2s ease',
                }}
                onClick={() => setStepResponseTab('extract')}
              >
                参数提取结果
              </Text>
            </Space>
            {stepResponseResult && (
              <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 16, fontSize: 13 }}>
                <span>
                  <span style={{ color: 'var(--au-slate-muted)' }}>Status: </span>
                  <span style={{ color: stepResponseResult.status < 400 ? 'var(--au-primary)' : 'var(--au-terracotta)', fontWeight: 700 }}>
                    {stepResponseResult.status}
                  </span>
                </span>
                <span>
                  <span style={{ color: 'var(--au-slate-muted)' }}>Time: </span>
                  <span style={{ color: 'var(--au-slate)', fontWeight: 600 }}>
                    {stepResponseResult.elapsed_ms}ms
                  </span>
                </span>
                <span>
                  <span style={{ color: 'var(--au-slate-muted)' }}>Result: </span>
                  <span style={{ color: stepResponseResult.status < 400 ? 'var(--au-primary)' : 'var(--au-terracotta)', fontWeight: 600 }}>
                    {stepResponseResult.status < 400 ? '成功' : '失败'}
                  </span>
                </span>
              </div>
            )}
          </div>
          <div style={{ flex: 1, overflow: 'auto', padding: '12px 16px' }}>
            {(() => {
              if (!stepResponseResult) {
                return (
                  <div style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'var(--au-text-faint)' }}>
                    <Empty
                      description={
                        <span>
                          点击 <span style={{ color: 'var(--au-primary)', cursor: 'pointer' }} onClick={handleStepDebug}>发送</span> 获取响应内容
                        </span>
                      }
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                  </div>
                )
              }
              if (stepResponseTab === 'body') {
                return (
                  <div style={{ position: 'relative' }}>
                    <Space style={{ position: 'absolute', right: 0, top: -4, zIndex: 1 }}>
                      <Button size="small" onClick={handleStepFormatResponse}>格式化</Button>
                      <Button size="small" onClick={() => { navigator.clipboard.writeText(stepResponseResult.body); message.success('已复制') }}>复制</Button>
                    </Space>
                    <pre style={{ margin: 0, fontSize: 13, fontFamily: "'JetBrains Mono', 'Fira Code', 'Consolas', monospace", whiteSpace: 'pre-wrap', wordBreak: 'break-all', lineHeight: 1.7, background: isDark ? 'var(--bg)' : 'var(--au-header-bg)', padding: '12px 16px', borderRadius: 6, border: isDark ? '1px solid var(--border)' : '1px solid var(--au-border)', maxHeight: '100%', overflow: 'auto', color: isDark ? 'var(--text)' : undefined }}>
                      {highlightStepJson(stepResponseResult.body)}
                    </pre>
                  </div>
                )
              }
              if (stepResponseTab === 'headers') {
                return (
                  <div style={{ border: isDark ? '1px solid var(--border)' : '1px solid var(--au-border)', borderRadius: 6, overflow: 'hidden', fontSize: 13 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr', padding: '8px 16px', background: isDark ? 'var(--bg)' : 'var(--au-row-alt)', borderBottom: isDark ? '1px solid var(--border)' : '1px solid var(--au-border)', fontWeight: 600, color: isDark ? 'var(--text-light)' : 'var(--au-slate)' }}>
                      <span>Header</span>
                      <span>Value</span>
                    </div>
                    {Object.entries(stepResponseResult.headers).map(([k, v], idx) => (
                      <div key={k} style={{ display: 'grid', gridTemplateColumns: '180px 1fr', padding: '6px 16px', background: isDark ? (idx % 2 === 0 ? 'var(--bg)' : 'var(--card)') : (idx % 2 === 0 ? '#ffffff' : 'var(--au-header-bg)'), borderBottom: idx < Object.entries(stepResponseResult.headers).length - 1 ? (isDark ? '1px solid var(--border)' : '1px solid var(--au-row-alt)') : 'none' }}>
                        <span style={{ color: isDark ? '#A5B4FC' : '#0369a1', fontWeight: 500, fontFamily: 'monospace' }}>{k}</span>
                        <span style={{ color: isDark ? 'var(--text)' : 'var(--au-slate)', wordBreak: 'break-all', fontFamily: 'monospace' }}>{v}</span>
                      </div>
                    ))}
                  </div>
                )
              }
              if (stepResponseTab === 'extract') {
                return (
                  <div>
                    {stepExtractResults.length === 0 ? (
                      <div style={{ padding: '40px 0', textAlign: 'center', color: isDark ? 'var(--text-lighter)' : 'var(--au-slate-muted)', fontSize: 14 }}>
                        暂无参数提取结果，请先在"参数提取"Tab中添加规则并发送请求
                      </div>
                    ) : (
                      <div style={{ border: isDark ? '1px solid var(--border)' : '1px solid var(--au-border)', borderRadius: 6, overflow: 'hidden', fontSize: 13 }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '60px 120px 100px 1fr', padding: '8px 16px', background: isDark ? 'var(--bg)' : 'var(--au-row-alt)', borderBottom: isDark ? '1px solid var(--border)' : '1px solid var(--au-border)', fontWeight: 600, color: isDark ? 'var(--text-light)' : 'var(--au-slate)' }}>
                          <span>结果</span>
                          <span>参数名</span>
                          <span>提取方式</span>
                          <span>提取详情</span>
                        </div>
                        {stepExtractResults.map((result, idx) => {
                          const rule = stepExtractRules.find((r) => r.id === result.id)
                          const methodLabel = rule ? stepExtractMethodOptions.find((o) => o.value === rule.method)?.label : ''
                          return (
                            <div key={result.id} style={{ display: 'grid', gridTemplateColumns: '60px 120px 100px 1fr', padding: '8px 16px', background: isDark ? (idx % 2 === 0 ? 'var(--bg)' : 'var(--card)') : (idx % 2 === 0 ? '#ffffff' : 'var(--au-header-bg)'), borderBottom: idx < stepExtractResults.length - 1 ? (isDark ? '1px solid var(--border)' : '1px solid var(--au-row-alt)') : 'none', alignItems: 'center', gap: 16 }}>
                              <Tag color={result.success ? 'success' : 'error'} style={{ width: 60, textAlign: 'center', margin: 0 }}>
                                {result.success ? '成功' : '失败'}
                              </Tag>
                              <span style={{ color: isDark ? '#A5B4FC' : '#0369a1', fontWeight: 500, fontFamily: 'monospace' }}>
                                {rule?.name || '(未命名)'}
                              </span>
                              <span style={{ color: isDark ? 'var(--text-light)' : 'var(--au-slate-muted)' }}>
                                {methodLabel}
                              </span>
                              <span style={{ color: isDark ? 'var(--text)' : 'var(--au-slate)', wordBreak: 'break-all' }}>
                                {result.message}
                              </span>
                            </div>
                          )
                        })}
                        <div style={{ padding: '8px 16px', background: isDark ? 'var(--bg)' : 'var(--au-header-bg)', borderTop: isDark ? '1px solid var(--border)' : '1px solid var(--au-border)', fontWeight: 600, color: isDark ? 'var(--text-light)' : 'var(--au-slate)' }}>
                          {stepExtractResults.filter((r) => r.success).length}/{stepExtractResults.length} 成功
                        </div>
                      </div>
                    )}
                  </div>
                )
              }
              return (
                <div>
                  {stepAssertionResults.length === 0 ? (
                    <div style={{ padding: '40px 0', textAlign: 'center', color: isDark ? 'var(--text-lighter)' : 'var(--au-slate-muted)', fontSize: 14 }}>
                      暂无断言结果，请先在"断言规则"Tab中添加规则并发送请求
                    </div>
                  ) : (
                    <div style={{ border: isDark ? '1px solid var(--border)' : '1px solid var(--au-border)', borderRadius: 6, overflow: 'hidden', fontSize: 13 }}>
                      <div style={{ display: 'flex', padding: '8px 16px', background: isDark ? 'var(--bg)' : 'var(--au-row-alt)', borderBottom: isDark ? '1px solid var(--border)' : '1px solid var(--au-border)', fontWeight: 600, color: isDark ? 'var(--text-light)' : 'var(--au-slate)', gap: 16 }}>
                        <span style={{ width: 60 }}>结果</span>
                        <span style={{ flex: 1 }}>断言详情</span>
                      </div>
                      {stepAssertionResults.map((result, idx) => {
                        const rule = stepAssertionRules.find((r) => r.id === result.id)
                        const typeLabel = rule ? stepAssertionTypeOptions.find((o) => o.value === rule.type)?.label : ''
                        return (
                          <div key={result.id} style={{ display: 'flex', padding: '8px 16px', background: isDark ? (idx % 2 === 0 ? 'var(--bg)' : 'var(--card)') : (idx % 2 === 0 ? '#ffffff' : 'var(--au-header-bg)'), borderBottom: idx < stepAssertionResults.length - 1 ? (isDark ? '1px solid var(--border)' : '1px solid var(--au-row-alt)') : 'none', alignItems: 'center', gap: 16 }}>
                            <Tag color={result.passed ? 'success' : 'error'} style={{ width: 60, textAlign: 'center', margin: 0 }}>
                              {result.passed ? '通过' : '失败'}
                            </Tag>
                            <span style={{ flex: 1, color: isDark ? 'var(--text)' : 'var(--au-slate)' }}>
                              {typeLabel && <Tag style={{ marginRight: 6 }}>{typeLabel}</Tag>}
                              {result.message}
                            </span>
                          </div>
                        )
                      })}
                      <div style={{ padding: '8px 16px', background: isDark ? 'var(--bg)' : 'var(--au-header-bg)', borderTop: isDark ? '1px solid var(--border)' : '1px solid var(--au-border)', fontWeight: 600, color: isDark ? 'var(--text-light)' : 'var(--au-slate)' }}>
                        {stepAssertionResults.filter((r) => r.passed).length}/{stepAssertionResults.length} 通过
                      </div>
                    </div>
                  )}
                </div>
              )
            })()
            }
          </div>
        </div>
      </Form>
    )
  }

  // ============ 表格列 ============

  const columns: ColumnsType<Scenario> = [
    {
      title: '场景名称',
      dataIndex: 'name',
      width: 160,
      render: (v: string, record: Scenario) => (
        <Button
          type="link"
          style={{ padding: 0, fontWeight: 500 }}
          onClick={() => handleEdit(record)}
        >
          {v}
        </Button>
      ),
    },
    {
      title: '场景描述',
      dataIndex: 'description',
      width: 220,
      ellipsis: true,
      render: (v: string) => v || <span style={{ color: 'var(--au-slate-muted)' }}>-</span>,
    },
    {
      title: '运行环境',
      dataIndex: 'environment',
      width: 200,
      render: (v: string) => v ? <a href={v} target="_blank" rel="noreferrer" style={{ wordBreak: 'break-all' }}>{v}</a> : <span style={{ color: 'var(--au-slate-muted)' }}>-</span>,
    },
    {
      title: '步骤数',
      dataIndex: 'step_count',
      width: 70,
      render: (v: number) => <span style={{ color: isDark ? 'var(--au-primary-light)' : 'var(--au-primary)', fontWeight: 500 }}>{v}</span>,
    },
    {
      title: '最后执行状态',
      dataIndex: 'last_execution_status',
      width: 100,
      render: (v: string | null) => {
        if (!v) return <span style={{ color: 'var(--au-slate-muted)' }}>-</span>
        return <Tag color={executionStatusColor[v] || 'default'}>{executionStatusText[v] || v}</Tag>
      },
    },
    {
      title: '最后执行时间',
      dataIndex: 'last_execution_time',
      width: 150,
      render: (v: string | null) => v || <span style={{ color: 'var(--au-slate-muted)' }}>-</span>,
    },
    {
      title: '操作',
      width: 180,
      render: (_: unknown, record: Scenario) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            className="run-btn"
            loading={!!executingIds[record.id]}
            disabled={!!executingIds[record.id]}
            onClick={() => { void handleExecute(record) }}
            style={{ padding: '0 4px', height: 22 }}
          >
            {executingIds[record.id] ? '正在执行' : '执行'}
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => handleEdit(record)}
            style={{ padding: '0 4px', height: 22 }}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定删除此场景？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
            okButtonProps={{ danger: true }}
          >
            <Button type="link" size="small" danger style={{ padding: '0 4px', height: 22 }}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ]

  // ============ 渲染 ============

  const StepNonHttpEditor = () => {
    const currentType = watchedStepType
    const stepTypeNum = typeof currentType === 'string'
      ? { http: 1, wait: 2, condition: 3, loop: 4, assertion: 5, db: 6, script: 7 }[currentType as ScenarioStepType] || 1
      : (currentType as number) || 1

    return (
      <div style={{ flex: 1, overflow: 'auto', padding: '16px 20px' }}>
        <Form
          form={stepForm}
          layout="vertical"
          onValuesChange={(changedValues) => {
            if ('name' in changedValues && selectedStepId !== null) {
              setSteps((prev) => prev.map((s) => (s.id === selectedStepId ? { ...s, name: changedValues.name } : s)))
            }
          }}
        >
          <Form.Item
            name="name"
            label="步骤名称"
            rules={[{ required: true, message: '请输入步骤名称' }]}
          >
            <Input placeholder="请输入步骤名称" maxLength={255} />
          </Form.Item>
          {/* 步骤类型在添加步骤时已选定，此处仅保留字段值用于保存，不再展示 */}
          <Form.Item name="step_type" hidden>
            <Select
              options={stepTypeOptions.map((opt) => ({
                value: opt.value,
                label: stepTypeConfig[opt.value as ScenarioStepType]?.label,
              }))}
            />
          </Form.Item>

          {stepTypeNum === 2 && (
            <>
              <Form.Item name="duration_ms" label="等待时长(毫秒)">
                <Input type="number" min={0} />
              </Form.Item>
              <Form.Item name="condition" label="等待条件">
                <Input placeholder="为空表示固定时长等待" />
              </Form.Item>
            </>
          )}

          {stepTypeNum === 3 && (
            <>
              <Form.Item name="expression" label="条件表达式">
                <Input.TextArea rows={4} placeholder="例如：${status} == 200" />
              </Form.Item>
              <Form.Item name="expected_value" label="期望值">
                <Input placeholder="条件为真时的期望值" />
              </Form.Item>
            </>
          )}

          {stepTypeNum === 4 && (
            <>
              <Form.Item name="loop_type" label="循环类型">
                <Select
                  options={[
                    { label: '次数循环', value: 'count' },
                    { label: '条件循环', value: 'while' },
                    { label: '遍历循环', value: 'for_each' },
                  ]}
                />
              </Form.Item>
              <Form.Item name="times" label="循环次数">
                <Input type="number" min={1} />
              </Form.Item>
              <Form.Item name="max_retries" label="最大重试次数">
                <Input type="number" min={0} />
              </Form.Item>
            </>
          )}

          {stepTypeNum === 5 && (
            <>
              <div style={{ marginBottom: 12, color: 'var(--au-slate-muted)', fontSize: 12 }}>断言规则</div>
              <Dropdown
                menu={{
                  items: stepAssertionTypeOptions.map((opt) => ({
                    key: opt.value,
                    label: opt.label,
                    onClick: () => addStepAssertionRule(opt.value),
                  })),
                }}
              >
                <Button type="primary" style={{ marginBottom: 12 }}>
                  添加断言规则
                </Button>
              </Dropdown>
              {stepAssertionRules.map((rule) => (
                <div
                  key={rule.id}
                  style={{
                    border: isDark ? '1px solid var(--border)' : '1px solid var(--au-border)',
                    borderRadius: 4,
                    padding: '6px 10px',
                    marginBottom: 6,
                    background: isDark ? 'var(--bg)' : 'var(--au-header-bg)',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Tag color="blue" style={{ margin: 0, flexShrink: 0 }}>{stepAssertionTypeOptions.find((o) => o.value === rule.type)?.label}</Tag>
                    {rule.type === 'response_header' && (
                      <AutoComplete
                        size="small"
                        placeholder="Header Key"
                        value={rule.key}
                        onChange={(v) => updateStepAssertionRule(rule.id, { key: v })}
                        style={{ width: 140 }}
                        options={stepCommonHeaders}
                        filterOption={(inputValue, option) =>
                          option!.value.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
                        }
                      />
                    )}
                    {rule.type === 'response_body' && (
                      <Input
                        size="small"
                        placeholder="表达式(选填)"
                        value={rule.expression}
                        onChange={(e) => updateStepAssertionRule(rule.id, { expression: e.target.value })}
                        style={{ width: 130 }}
                      />
                    )}
                    <Select
                      size="small"
                      value={rule.operator}
                      onChange={(v) => updateStepAssertionRule(rule.id, { operator: v })}
                      style={{ width: 100 }}
                      options={stepAssertionOperatorMap[rule.type] || []}
                    />
                    {!['is_empty', 'is_not_empty', 'is_null', 'is_not_null', 'field_exists', 'field_not_exists'].includes(rule.operator) && (
                      <Input
                        size="small"
                        placeholder={
                          rule.type === 'response_time' ? '毫秒数' :
                          rule.operator === 'regex_match' ? '正则表达式' :
                          rule.operator === 'type_is' ? '类型名' :
                          rule.operator?.startsWith('length') ? '长度值' :
                          rule.operator?.startsWith('greater') || rule.operator?.startsWith('less') ? '数值' :
                          '预期值'
                        }
                        value={rule.expected}
                        onChange={(e) => updateStepAssertionRule(rule.id, { expected: e.target.value })}
                        style={{ flex: 1 }}
                      />
                    )}
                    <Button
                      type="text"
                      size="small"
                      danger
                      onClick={() => removeStepAssertionRule(rule.id)}
                      style={{ padding: '0 4px', flexShrink: 0 }}
                    >
                      删除
                    </Button>
                  </div>
                </div>
              ))}
            </>
          )}

          {stepTypeNum === 6 && (
            <>
              <Form.Item name="datasource_id" label="数据源 ID">
                <Input type="number" min={1} />
              </Form.Item>
              <Form.Item name="sql_type" label="SQL 类型">
                <Select
                  options={[
                    { label: '查询', value: 'select' },
                    { label: '插入', value: 'insert' },
                    { label: '更新', value: 'update' },
                    { label: '删除', value: 'delete' },
                  ]}
                />
              </Form.Item>
              <Form.Item name="sql" label="SQL 语句">
                <Input.TextArea rows={6} placeholder="SELECT * FROM ..." />
              </Form.Item>
              <Form.Item name="expected" label="期望结果">
                <Input.TextArea rows={3} placeholder="JSON 格式" />
              </Form.Item>
            </>
          )}

          {stepTypeNum === 7 && (
            <>
              <Form.Item name="language" label="脚本语言">
                <Select
                  options={[
                    { label: 'JavaScript', value: 'javascript' },
                    { label: 'Python', value: 'python' },
                    { label: 'SQL', value: 'sql' },
                  ]}
                />
              </Form.Item>
              <Form.Item name="content" label="脚本内容">
                <Input.TextArea rows={8} placeholder="// 脚本内容" style={{ fontFamily: 'monospace' }} />
              </Form.Item>
              <Form.Item name="timeout" label="超时时间(秒)">
                <Input type="number" min={1} />
              </Form.Item>
            </>
          )}
        </Form>
      </div>
    )
  }

  return (
    <div className="aurora-page aurora-page--fit">
      {/* 页面标题 */}
      <div className="page-header" style={{ flexShrink: 0 }}>
        <Title level={4} style={{ margin: 0, fontWeight: 600 }}>
          场景编排
        </Title>
        <Space>
          <Button type="primary" onClick={handleCreate}>
            新增场景
          </Button>
        </Space>
      </div>

      <Row gutter={16} style={{ flex: 1, overflow: 'hidden' }}>
        {/* 左侧模块树 */}
        <Col xs={24} md={5} lg={4} style={{ height: '100%' }}>
          <div className="content-card" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div className="content-card-header" style={{ flexShrink: 0 }}>
              <div className="content-card-title">
                <FolderOutlined style={{ marginRight: 8, color: 'var(--au-primary)' }} />
                场景模块
              </div>
              <Button
                type="text"
                size="small"
                icon={<PlusOutlined />}
                onClick={() => openCreateModuleModal(null)}
              />
            </div>
            <div style={{ flex: 1, overflow: 'auto', padding: '8px 12px' }}>
              {treeData.length > 0 ? (
                <Dropdown
                  menu={{ items: contextMenuModule ? buildContextMenuItems(contextMenuModule) : [] }}
                  trigger={['contextMenu']}
                  open={contextMenuModule !== null}
                  onOpenChange={(open) => { if (!open) setContextMenuModule(null) }}
                >
                  <div>
                    <Tree
                      treeData={treeData}
                      defaultExpandAll
                      onSelect={(keys) => {
                        setSelectedModule(keys[0] ? Number(keys[0]) : null)
                        setPage(1)
                      }}
                      onRightClick={handleTreeRightClick}
                      showLine={{ showLeafIcon: false }}
                    />
                  </div>
                </Dropdown>
              ) : (
                <Empty description="暂无模块" />
              )}
            </div>
          </div>
        </Col>

        {/* 右侧场景列表 */}
        <Col xs={24} md={19} lg={20} style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* 搜索栏 */}
          <div className="search-bar" style={{ flexShrink: 0 }}>
            <Row gutter={[12, 0]} align="middle">
              <Col flex="auto">
                <Input
                  placeholder="搜索场景名称"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  onPressEnter={handleSearch}
                  allowClear
                  style={{ minWidth: 100 }}
                />
              </Col>
              <Col>
                <Button
                  type="primary"
                  onClick={handleSearch}
                  style={{ background: 'var(--au-primary)', borderColor: 'var(--au-primary)', color: '#fff' }}
                >
                  查询
                </Button>
              </Col>
            </Row>
          </div>

          {/* 表格 */}
          <div className="content-card" style={{ flex: 1, overflow: 'hidden' }}>
            <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
              <Table
                columns={columns}
                dataSource={scenarios}
                rowKey="id"
                loading={loading}
                size="middle"
                scroll={{ y: '100%' }}
                pagination={{
                  current: page,
                  pageSize,
                  total,
                  showSizeChanger: true,
                  showTotal: (t) => `共 ${t} 条`,
                  onChange: (p, ps) => {
                    setPage(p)
                    setPageSize(ps)
                  },
                }}
              />
            </div>
          </div>
        </Col>

        {/* 场景新增/编辑抽屉 */}
        <Drawer
          title={null}
          open={drawerOpen}
          onClose={() => {
            setDrawerOpen(false)
            setEditingScenario(null)
            setSteps([])
            setSelectedStepIdSync(null)
            scenarioForm.resetFields()
            stepForm.resetFields()
            setStepRunResults({})
            setStepRunAssertionResults({})
            setStepRunExtractResults({})
            setStepRunStatus({})
            // 清空步骤详情缓存，避免下次打开时使用旧数据
            stepDetailCacheRef.current.clear()
            setStepDetailLoading(false)
          }}
          placement="right"
          width="75%"
          rootClassName="aurora-edit-drawer"
          rootStyle={{ top: 56, height: 'calc(100vh - 56px)' }}
          maskStyle={{ top: 56, height: 'calc(100vh - 56px)', background: 'transparent' }}
          bodyStyle={{ padding: 0, display: 'flex', flexDirection: 'column' }}
          headerStyle={{ display: 'none' }}
        >
          {/* 顶部标题栏 */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 24px',
            borderBottom: isDark ? '1px solid var(--border)' : '1px solid var(--au-border)',
            background: isDark ? 'var(--card)' : '#ffffff',
            flexShrink: 0,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <BranchesOutlined style={{ fontSize: 18, color: 'var(--au-primary)' }} />
              <Title level={5} style={{ margin: 0, fontFamily: "'Noto Serif SC', serif", fontWeight: 700, color: 'var(--au-primary-dark)' }}>
                {editingScenario ? '编辑场景' : '新增场景'}
              </Title>
            </div>
            <Space>
              <Button onClick={() => {
                setDrawerOpen(false)
                setEditingScenario(null)
                setSteps([])
                setSelectedStepIdSync(null)
                setStepRunResults({})
                setStepRunAssertionResults({})
                setStepRunExtractResults({})
                setStepRunStatus({})
              }}>
                取消
              </Button>
              <Button
                loading={scenarioRunning}
                onClick={() => { void handleRunScenario() }}
                disabled={!editingScenario || steps.length === 0}
                style={{ borderColor: 'var(--au-primary)', color: 'var(--au-primary)' }}
              >
                运行场景
              </Button>
              <Button
                type="primary"
                loading={submitting}
                onClick={handleSave}
                style={{ background: 'var(--au-primary)', border: 'none' }}
              >
                保存场景
              </Button>
            </Space>
          </div>

          {/* 场景基本信息（白底表单条，参考 scenario-form-bar） */}
          <div style={{
            padding: '16px 24px',
            borderBottom: isDark ? '1px solid var(--border)' : '1px solid var(--au-border)',
            background: 'var(--au-surface)',
            flexShrink: 0,
          }}>
            <Form
              form={scenarioForm}
              layout="inline"
              style={{ gap: 12, flexWrap: 'wrap' }}
              onValuesChange={(changedValues) => {
                if ('environment' in changedValues && editingScenario) {
                  const newEnv = changedValues.environment as string
                  setEditingScenario({ ...editingScenario, environment: newEnv })
                  const currentStep = steps.find((s) => s.id === selectedStepId)
                  if (currentStep?.step_type === 'http') {
                    stepForm.setFieldsValue({ host: newEnv })
                    if (selectedImportApi) {
                      setSelectedImportApi({ ...selectedImportApi, host: newEnv })
                    }
                  }
                }
              }}
            >
              <Form.Item
                name="name"
                label="场景名称"
                rules={[{ required: true, message: '请输入场景名称' }]}
                style={{ flex: '0 0 auto', marginBottom: 0 }}
              >
                <Input placeholder="请输入场景名称" maxLength={255} style={{ width: 160 }} />
              </Form.Item>
              <Form.Item name="environment" label="运行环境" style={{ minWidth: 220, marginBottom: 0 }}>
                <Select
                  placeholder="请选择运行环境"
                  options={envOptions}
                  notFoundContent="当前项目暂无环境，请先在环境管理中创建"
                />
              </Form.Item>
              <Form.Item name="module_id" label="所属模块" style={{ minWidth: 220, marginBottom: 0 }}>
                <TreeSelect
                  allowClear
                  placeholder="选择模块"
                  treeDefaultExpandAll
                  treeData={moduleSelectTreeData}
                />
              </Form.Item>
              <Form.Item name="description" label="描述" style={{ flex: '2 1 300px', marginBottom: 0 }}>
                <Input placeholder="场景描述（选填）" />
              </Form.Item>
            </Form>
          </div>

          {/* 左右分栏：步骤列表 | 步骤详情 */}
          <div style={{
            flex: 1,
            display: 'flex',
            overflow: 'hidden',
          }}>
            {/* 左侧：步骤列表 */}
            <div style={{
              width: 260,
              minWidth: 220,
              borderRight: isDark ? '1px solid var(--border)' : '1px solid var(--au-border)',
              display: 'flex',
              flexDirection: 'column',
              background: isDark ? 'var(--card)' : '#ffffff',
              flexShrink: 0,
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '12px 16px',
                borderBottom: isDark ? '1px solid var(--border)' : '1px solid var(--au-border)',
              }}>
                <Text strong style={{ fontSize: 15, fontFamily: "'Noto Serif SC', serif", color: 'var(--au-primary-dark)' }}>步骤列表</Text>
              </div>
              <div style={{ flex: 1, overflow: 'auto', padding: '8px 0' }}>
                {!editingScenario ? (
                  <div style={{ padding: 24, textAlign: 'center', color: 'var(--au-slate-muted)' }}>
                    <Empty description="请先保存场景后添加步骤" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                  </div>
                ) : stepsLoading ? (
                  <div style={{ padding: 24, textAlign: 'center' }}>
                    <Text type="secondary">加载中...</Text>
                  </div>
                ) : steps.length === 0 ? (
                  <div style={{ padding: 24, textAlign: 'center' }}>
                    <Empty description="暂无步骤，点击下方按钮添加" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                    <Dropdown
                      menu={{
                        items: stepCreateMenuItems,
                        onClick: ({ key }) => handleAddStepByType(key as StepCreateAction),
                      }}
                      trigger={['click']}
                    >
                      <Button type="dashed" block style={{ marginTop: 12 }}>
                        添加步骤
                      </Button>
                    </Dropdown>
                  </div>
                ) : (
                  <>
                    {steps.filter((s) => !s.parent_id).map((step, idx) => {
                      const config = stepTypeConfig[step.step_type] || stepTypeConfig.http
                      const isSelected = selectedStepId === step.id
                      const runStatus = stepRunStatus[step.id]
                      const statusTag = runStatus ? (
                        runStatus.status === 'running' ? (
                          <span style={{ fontSize: 11, color: 'var(--au-primary)' }}>运行中...</span>
                        ) : runStatus.status === 'success' ? (
                          <span style={{ fontSize: 11, color: 'var(--au-primary)' }}>成功</span>
                        ) : runStatus.status === 'failed' ? (
                          <span style={{ fontSize: 11, color: 'var(--au-terracotta)' }}>失败</span>
                        ) : runStatus.status === 'skipped' ? (
                          <span style={{ fontSize: 11, color: 'var(--au-slate-muted)' }}>已跳过</span>
                        ) : (
                          <span style={{ fontSize: 11, color: 'var(--au-slate-muted)' }}>待运行</span>
                        )
                      ) : null
                      // 条件/循环/断言控制步骤：可展开子步骤作用域
                      const childSteps = steps
                        .filter((s) => s.parent_id === step.id)
                        .sort((a, b) => a.sort - b.sort)
                      const canExpand = ['condition', 'loop', 'assertion'].includes(step.step_type)
                      const isExpanded = !!expandedStepIds[step.id]
                      return (
                        <div key={step.id}>
                          <div
                            draggable
                            onDragStart={(e) => handleDragStart(e, step.id)}
                            onDragOver={(e) => handleDragOver(e, step, idx)}
                            onDrop={(e) => handleDrop(e, step, idx)}
                            onDragEnd={handleDragEnd}
                            onClick={() => handleSelectStep(step)}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              padding: '10px 16px',
                              cursor: 'pointer',
                              background: isSelected
                                ? 'var(--au-primary-100)'
                                : dropIndicator?.stepId === step.id && dropIndicator.mode === 'inside'
                                  ? (isDark ? 'rgba(82,139,255,0.15)' : 'rgba(82,139,255,0.08)')
                                  : 'transparent',
                              borderLeft: isSelected ? '3px solid var(--au-primary)' : '3px solid transparent',
                              borderTop: dragOverIndex === idx ? '2px solid var(--au-primary)' : 'none',
                              outline: dropIndicator?.stepId === step.id && dropIndicator.mode === 'inside' ? '2px dashed var(--au-primary)' : 'none',
                              outlineOffset: '-2px',
                              transition: 'background 0.15s, border-top 0.1s',
                            }}
                            onMouseEnter={(e) => {
                              if (!isSelected) (e.currentTarget as HTMLElement).style.background = 'var(--au-primary-50)'
                            }}
                            onMouseLeave={(e) => {
                              if (!isSelected) (e.currentTarget as HTMLElement).style.background = 'transparent'
                            }}
                          >
                            {canExpand && (
                              <Button
                                type="text"
                                size="small"
                                icon={isExpanded ? <DownOutlined /> : <RightOutlined />}
                                onClick={(e) => {
                                  e.stopPropagation()
                                  setExpandedStepIds((prev) => ({ ...prev, [step.id]: !prev[step.id] }))
                                }}
                                style={{ padding: 0, width: 18, height: 18, marginRight: 2, flexShrink: 0, opacity: 0.55 }}
                                title="展开/收起子步骤"
                              />
                            )}
                            <div
                              title="拖拽以调整顺序"
                              onClick={(e) => e.stopPropagation()}
                              style={{
                                cursor: 'grab',
                                color: 'var(--au-slate-muted)',
                                marginRight: 6,
                                display: 'flex',
                                alignItems: 'center',
                                flexShrink: 0,
                              }}
                            >
                              <HolderOutlined />
                            </div>
                            <div style={{
                              width: 28,
                              height: 28,
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              background: isSelected ? 'var(--au-primary)' : (isDark ? `${config.color}30` : `${config.color}15`),
                              color: isSelected ? '#fff' : config.color,
                              fontSize: 14,
                              flexShrink: 0,
                              marginRight: 10,
                              transition: 'background 0.2s ease, color 0.2s ease',
                            }}>
                              {config.icon}
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 13, fontWeight: 600, color: isSelected ? 'var(--au-primary-dark)' : (isDark ? 'var(--text)' : 'var(--au-slate)'), display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{step.name || `步骤 ${idx + 1}`}</span>
                                {statusTag}
                              </div>
                              <div style={{ fontSize: 11, color: 'var(--au-slate-muted)', marginTop: 2 }}>
                                {config.label}
                              </div>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', flexShrink: 0, marginRight: 4 }}>
                              <Button
                                type="text"
                                size="small"
                                icon={<ArrowUpOutlined />}
                                disabled={idx === 0}
                                onClick={(e) => { e.stopPropagation(); handleMoveStep(step.id, 'up') }}
                                style={{ padding: '0 4px', height: 16, fontSize: 10, opacity: 0.5 }}
                                title="上移"
                              />
                              <Button
                                type="text"
                                size="small"
                                icon={<ArrowDownOutlined />}
                                disabled={idx === steps.filter((s) => !s.parent_id).length - 1}
                                onClick={(e) => { e.stopPropagation(); handleMoveStep(step.id, 'down') }}
                                style={{ padding: '0 4px', height: 16, fontSize: 10, opacity: 0.5 }}
                                title="下移"
                              />
                            </div>
                            <Popconfirm
                              title="确定删除此步骤？"
                              onConfirm={(e) => {
                                e?.stopPropagation()
                                handleDeleteStep(step.id)
                              }}
                              onCancel={(e) => e?.stopPropagation()}
                              okText="确定"
                              cancelText="取消"
                              okButtonProps={{ danger: true }}
                            >
                              <Button
                                type="text"
                                size="small"
                                danger
                                icon={<DeleteOutlined />}
                                onClick={(e) => e.stopPropagation()}
                                style={{ opacity: 0.5, flexShrink: 0 }}
                              />
                            </Popconfirm>
                          </div>
                          {/* 子步骤（控制步骤的作用域内步骤）：点击左侧箭头展开/收起，区域内可添加/拖入子步骤 */}
                          {canExpand && isExpanded && (
                            <div
                              onDragOver={(e) => {
                                e.preventDefault()
                                e.stopPropagation()
                                e.dataTransfer.dropEffect = 'move'
                                dropModeRef.current = 'inside'
                                if (dropIndicator?.stepId !== step.id || dropIndicator.mode !== 'inside') {
                                  setDropIndicator({ stepId: step.id, mode: 'inside' })
                                }
                              }}
                              onDrop={(e) => { e.stopPropagation(); handleDrop(e, step, idx, 'inside') }}
                              style={{
                                margin: '2px 12px 6px 38px',
                                borderLeft: isDark ? '2px solid var(--border)' : '2px solid var(--au-border)',
                                background: dropIndicator?.stepId === step.id && dropIndicator.mode === 'inside'
                                  ? (isDark ? 'rgba(82,139,255,0.08)' : 'rgba(82,139,255,0.05)')
                                  : 'transparent',
                                transition: 'background 0.15s',
                              }}
                            >
                              {childSteps.map((child) => {
                                const childConfig = stepTypeConfig[child.step_type] || stepTypeConfig.http
                                const childSelected = selectedStepId === child.id
                                const childRunStatus = stepRunStatus[child.id]
                                const childStatusTag = childRunStatus ? (
                                  childRunStatus.status === 'running' ? (
                                    <span style={{ fontSize: 11, color: 'var(--au-primary)' }}>运行中...</span>
                                  ) : childRunStatus.status === 'success' ? (
                                    <span style={{ fontSize: 11, color: 'var(--au-primary)' }}>成功</span>
                                  ) : childRunStatus.status === 'failed' ? (
                                    <span style={{ fontSize: 11, color: 'var(--au-terracotta)' }}>失败</span>
                                  ) : childRunStatus.status === 'skipped' ? (
                                    <span style={{ fontSize: 11, color: 'var(--au-slate-muted)' }}>已跳过</span>
                                  ) : (
                                    <span style={{ fontSize: 11, color: 'var(--au-slate-muted)' }}>待运行</span>
                                  )
                                ) : null
                                return (
                                  <div
                                    key={child.id}
                                    draggable
                                    onDragStart={(e) => handleDragStart(e, child.id)}
                                    onDragEnd={handleDragEnd}
                                    onClick={() => handleSelectStep(child)}
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      padding: '10px 8px',
                                      cursor: 'pointer',
                                      background: childSelected ? 'var(--au-primary-100)' : 'transparent',
                                      borderLeft: childSelected ? '3px solid var(--au-primary)' : '3px solid transparent',
                                      transition: 'background 0.15s',
                                    }}
                                    onMouseEnter={(e) => {
                                      if (!childSelected) (e.currentTarget as HTMLElement).style.background = 'var(--au-primary-50)'
                                    }}
                                    onMouseLeave={(e) => {
                                      if (!childSelected) (e.currentTarget as HTMLElement).style.background = 'transparent'
                                    }}
                                  >
                                    <div style={{
                                      width: 22,
                                      height: 22,
                                      borderRadius: '50%',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      background: childSelected ? 'var(--au-primary)' : (isDark ? `${childConfig.color}30` : `${childConfig.color}15`),
                                      color: childSelected ? '#fff' : childConfig.color,
                                      fontSize: 12,
                                      flexShrink: 0,
                                      marginRight: 8,
                                      transition: 'background 0.2s ease, color 0.2s ease',
                                    }}>
                                      {childConfig.icon}
                                    </div>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                      <div style={{ fontSize: 13, fontWeight: 600, color: childSelected ? 'var(--au-primary-dark)' : (isDark ? 'var(--text)' : 'var(--au-slate)'), display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{child.name}</span>
                                        {childStatusTag}
                                      </div>
                                      <div style={{ fontSize: 11, color: 'var(--au-slate-muted)', marginTop: 2 }}>
                                        {childConfig.label}
                                      </div>
                                    </div>
                                    <Popconfirm
                                      title="确定删除此步骤？"
                                      onConfirm={(e) => {
                                        e?.stopPropagation()
                                        handleDeleteStep(child.id)
                                      }}
                                      onCancel={(e) => e?.stopPropagation()}
                                      okText="确定"
                                      cancelText="取消"
                                      okButtonProps={{ danger: true }}
                                    >
                                      <Button
                                        type="text"
                                        size="small"
                                        danger
                                        icon={<DeleteOutlined />}
                                        onClick={(e) => e.stopPropagation()}
                                        style={{ opacity: 0.5, flexShrink: 0 }}
                                      />
                                    </Popconfirm>
                                  </div>
                                )
                              })}
                              {/* 在作用域内添加子步骤 */}
                              {editingScenario && (
                                <div style={{ padding: '4px 8px 8px' }}>
                                  <Dropdown
                                    menu={{
                                      items: childStepCreateMenuItems,
                                      onClick: ({ key }) => handleAddStepByType(key as StepCreateAction, step.id),
                                    }}
                                    trigger={['click']}
                                  >
                                    <Button type="dashed" block onClick={(e) => e.stopPropagation()} style={{ height: 32 }}>
                                      添加子步骤
                                    </Button>
                                  </Dropdown>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )
                    })}
                    {/* 底部：添加步骤按钮（也可作为子步骤拖出的投放区，追加到顶层末尾） */}
                    {editingScenario && (
                      <div
                        style={{ padding: '10px 16px 14px' }}
                        onDragOver={(e) => {
                          e.preventDefault()
                          e.dataTransfer.dropEffect = 'move'
                          dropModeRef.current = 'before'
                        }}
                        onDrop={(e) => handleDropToBottom(e)}
                      >
                        <Dropdown
                          menu={{
                            items: stepCreateMenuItems,
                            onClick: ({ key }) => handleAddStepByType(key as StepCreateAction),
                          }}
                          trigger={['click']}
                        >
                          <Button type="dashed" block>
                            添加步骤
                          </Button>
                        </Dropdown>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* 右侧：步骤详情 */}
            <div style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              background: isDark ? 'var(--bg)' : 'var(--au-header-bg)',
              overflow: 'hidden',
            }}>
              {!editingScenario ? (
                <div style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <Empty description="请先保存场景" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                </div>
              ) : !selectedStepId ? (
                <div style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <Empty description="请在左侧选择一个步骤" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                </div>
              ) : (
                <>
                  <div style={{
                    padding: '6px 20px',
                    borderBottom: isDark ? '1px solid var(--border)' : '1px solid var(--au-border)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {(() => {
                        const selectedStep = steps.find((s) => s.id === selectedStepId)
                        const cfg = selectedStep ? stepTypeConfig[selectedStep.step_type] : null
                        return cfg ? (
                          <span style={{ color: cfg.color }}>{cfg.icon}</span>
                        ) : null
                      })()}
                      <Text strong style={{ fontSize: 15, fontFamily: "'Noto Serif SC', serif", color: 'var(--au-primary-dark)' }}>步骤配置</Text>
                    </div>
                    <Button
                      type="primary"
                      onClick={handleSaveStep}
                      style={{ background: 'var(--au-primary)', border: 'none', height: 34, padding: '0 20px' }}
                    >
                      保存步骤
                    </Button>
                  </div>

                  {stepDetailLoading ? (
                    <div style={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      <Spin tip="加载步骤详情中..." size="large" />
                    </div>
                  ) : stepDetailMode === 'api_detail' || stepDetailMode === 'new_request' ? (
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                      {renderStepApiEditor()}
                    </div>
                  ) : selectedStepId ? (
                    /* 直接调用渲染函数（而非组件）：
                       断言规则等主组件 state 变化触发重渲染时，避免因函数引用变化
                       导致子树被 React 卸载重挂、输入框失焦 */
                    StepNonHttpEditor()
                  ) : (
                    <div style={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      <Empty description="请在左侧选择一个步骤" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </Drawer>

        {/* 接口导入弹框 */}
        <Modal
          title="选择接口"
          open={apiImportModalOpen}
          onCancel={() => {
            setApiImportModalOpen(false)
            setSelectedImportApi(null)
          }}
          width={800}
          wrapClassName="aurora-modal-wrap"
          className="aurora-modal"
          footer={[
            <Button key="cancel" onClick={() => {
              setApiImportModalOpen(false)
              setSelectedImportApi(null)
            }}>
              取消
            </Button>,
            <Button
              key="confirm"
              type="primary"
              disabled={!selectedImportApi}
              onClick={async () => {
                if (!editingScenario || !selectedImportApi) return
                const parentId = importParentStepId
                const tempId = -(Date.now())
                const newSort = parentId
                  ? steps.filter((s) => s.parent_id === parentId).length
                  : steps.filter((s) => !s.parent_id).length
                const tempStep: ScenarioStep = {
                  id: tempId,
                  scenario_id: editingScenario.id,
                  name: selectedImportApi.name,
                  step_type: 'http',
                  sort: newSort,
                  parent_id: parentId,
                  create_time: '',
                  update_time: '',
                }
                // 导入为子步骤时自动展开父步骤的作用域区域
                if (parentId) {
                  setExpandedStepIds((prev) => ({ ...prev, [parentId]: true }))
                }
                setSteps((prev) => [...prev, tempStep])
                setSelectedStepIdSync(tempId)
                setStepActiveTab('headers')
                setStepBodyType('none')
                setStepResponseResult(null)
                setStepResponseTab('body')
                setStepAssertionRules([])
                setStepAssertionResults([])
                // 参数提取规则随新导入接口重置，避免残留上一个步骤的提取配置
                setStepExtractRules([])
                setStepExtractResults([])
                setSelectedImportApi(selectedImportApi)
                stepForm.setFieldsValue({
                  name: selectedImportApi.name,
                  step_type: 1,
                  method: selectedImportApi.method || 'GET',
                  path: selectedImportApi.path || '',
                  host: selectedImportApi.host || '',
                  headerEntries: jsonToKvArray(selectedImportApi.headers || '[]'),
                  paramEntries: jsonToKvArray(selectedImportApi.query_params || '[]'),
                  restEntries: jsonToKvArray(selectedImportApi.rest_params || '[]'),
                  body_type: selectedImportApi.body_type || 'none',
                  body: selectedImportApi.body || '',
                  bodyFormEntries: loadBodyFormEntries(selectedImportApi.body || '[]', selectedImportApi.body_type),
                  description: selectedImportApi.description || '',
                })
                applyStepHost()
                setStepBodyType(selectedImportApi.body_type || 'none')
                setStepDetailMode('api_detail')
                tempStepSnapshotsRef.current[tempId] = {
                  api: selectedImportApi,
                  detailMode: 'api_detail',
                  formValues: stepForm.getFieldsValue(true),
                  assertionRules: [],
                  extractRules: [],
                  bodyType: selectedImportApi.body_type || 'none',
                }
                setApiImportModalOpen(false)
              }}
            >
              确认
            </Button>,
          ]}
        >
          <Table
            rowKey="id"
            loading={apiListLoading}
            dataSource={apiList}
            rowSelection={{
              type: 'radio',
              selectedRowKeys: selectedImportApi ? [selectedImportApi.id] : [],
              onChange: (keys: React.Key[]) => {
                const api = apiList.find((a) => a.id === keys[0]) || null
                setSelectedImportApi(api)
              },
            }}
            columns={[
              {
                title: '接口名称',
                dataIndex: 'name',
                width: 200,
              },
              {
                title: '方法',
                dataIndex: 'method',
                width: 80,
                render: (v: string) => {
                  const colorMap: Record<string, string> = { GET: 'blue', POST: 'success', PUT: 'orange', DELETE: 'red', PATCH: 'purple' }
                  return <Tag color={colorMap[v] || 'default'}>{v}</Tag>
                },
              },
              {
                title: '路径',
                dataIndex: 'path',
                ellipsis: true,
              },
            ]}
            pagination={{
              current: apiListPage,
              pageSize: 10,
              total: apiListTotal,
              onChange: (p) => loadApiList(p),
            }}
            size="small"
          />
        </Modal>

        {/* 模块新增/编辑弹窗 */}
        <Modal
          title={editingModule ? '修改模块名称' : '新增场景模块'}
          open={moduleModalOpen}
          onOk={handleModuleSubmit}
          onCancel={() => {
            setModuleModalOpen(false)
            moduleForm.resetFields()
            setEditingModule(null)
          }}
          width={480}
          okText="保存"
          cancelText="取消"
          wrapClassName="aurora-modal-wrap"
          className="aurora-modal"
          destroyOnClose
        >
          <Form form={moduleForm} layout="vertical" style={{ marginTop: 4 }}>
            {!editingModule && (
              <Form.Item name="parent_id" label="上级模块">
                <TreeSelect
                  allowClear
                  placeholder="留空则为根模块"
                  treeDefaultExpandAll
                  treeData={moduleSelectTreeData}
                />
              </Form.Item>
            )}
            <Form.Item name="name" label="模块名称" rules={[{ required: true, message: '请输入模块名称' }]}>
              <Input placeholder="请输入模块名称" maxLength={128} />
            </Form.Item>
          </Form>
        </Modal>
      </Row>
    </div>
  )
}
